import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import API, { getApiUrl } from '../../../../utils/api';
import PromocionesService from '../../../../services/PromocionesService';
import comandaService from '../../../../services/ComandaService';
import CreacionPedidoActualizado from '../../CreacionPedido/CreacionPedido';
import TomaPedidos from '../../TomaPedidos';
import FormaPago from '../../Pago/FormaPago';
import ConfirmationModal from '../../../Biometrico/ConfirmationModal';
import CambioMesaModal from '../../shared/modals/CambioMesaModal';
import './GestionPedidoBase.css';

/**
 * Componente base para gestión de pedidos
 * Reutilizable para todos los tipos de pedido: Mesa, Llevar, Domicilio, Uber, Rappi, Delibot
 */
const GestionPedidoBase = ({
  // Configuración del tipo de pedido
  tipoPedido = 'mesa', // 'mesa', 'llevar', 'domicilio', 'uber', 'rappi', 'delibot'

  // Datos del pedido
  datosPedido = {},

  // Configuración de características
  configuracion = {
    permitirCambioMesa: false,
    permitirCambioMesero: false,
    permitirCambioPersonas: false,
    permitirDatosCliente: false,
    permitirDireccion: false,
    permitirRepartidor: false,
    requiereImpresion: true,
    permitirPagoMultiple: true
  },

  // Callbacks opcionales
  onPedidoCreado = null,
  onPedidoActualizado = null,
  onPedidoCerrado = null,

  // Validaciones personalizadas
  validacionesPersonalizadas = {},

  // Función para editar cliente (específico para llevar)
  onEditarCliente = null,

  // Ruta de regreso personalizada
  rutaRegreso = '/ventas'
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  // Estados principales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estado para información del negocio
  const [infoNegocio, setInfoNegocio] = useState({
    nombre_negocio: 'KAPITAL SUSHI',
    nombre_sucursal: '',
    direccion_linea1: 'AV. JOSE MARIA CHAVEZ #2303',
    direccion_linea2: 'COL. SAN FRANCISCO DEL ARENAL',
    razon_social: 'OPERADORA ASR S.A DE C.V.',
    rfc: 'OAC1404108V8'
  });

  // Estados del pedido
  const [infoPedido, setInfoPedido] = useState({
    idPedido: datosPedido?.idPedido || null,
    numeroPedido: datosPedido?.numeroPedido || 'N/A',
    status: datosPedido?.status || 'abierto',
    total: 0,
    subtotal: 0,
    descuentos: 0,
    pedidoExistente: datosPedido?.pedidoExistente || {},
    // Agregar función de editar cliente si está disponible
    onEditarCliente: onEditarCliente || null,
    ...datosPedido
  });

  // Estados de productos
  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [pedidoActual, setPedidoActual] = useState([]);
  const [productosNuevos, setProductosNuevos] = useState([]);
  const [productosGuardados, setProductosGuardados] = useState([]);
  const [promociones, setPromociones] = useState([]);
  const [promocionesAplicadas, setPromocionesAplicadas] = useState([]);
  const [calculandoPromociones, setCalculandoPromociones] = useState(false);

  // Estado para aplicar promociones de mesa en pedidos para llevar
  const [promocionMesaActiva, setPromocionMesaActiva] = useState(false);

  // Estados de UI
  const [mostrarConfirmacionFinalizar, setMostrarConfirmacionFinalizar] = useState(false);
  const [mostrarConfirmacionCerrar, setMostrarConfirmacionCerrar] = useState(false);
  const [mostrarConfirmacionPago, setMostrarConfirmacionPago] = useState(false);
  const [mostrarModalCambioMesa, setMostrarModalCambioMesa] = useState(false);
  const [loadingAccion, setLoadingAccion] = useState(false);

  // Estados para actualización automática
  const [actualizandoEnSegundoPlano, setActualizandoEnSegundoPlano] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
  const [ultimoCambioStatus, setUltimoCambioStatus] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Actualizar infoPedido cuando cambien los datosPedido
  useEffect(() => {
    console.log('🔄 ===========================================');
    console.log('🔄 useEffect de datosPedido se disparó');
    console.log('🔄 datosPedido recibido:', datosPedido);
    console.log('🔄 numeroPersonas en datosPedido:', datosPedido?.numeroPersonas);
    console.log('🔄 ===========================================');

    setInfoPedido(prev => {
      // Si acabamos de cambiar el status recientemente (menos de 30 segundos), no lo sobrescribir
      const ahora = new Date();
      const tiempoDesdeUltimoCambio = ultimoCambioStatus ? (ahora - ultimoCambioStatus) : Infinity;
      const preservarStatus = tiempoDesdeUltimoCambio < 30000; // 30 segundos

      console.log(`🔍 ===== useEffect datosPedido =====`);
      console.log(`  - Preservar status: ${preservarStatus}`);
      console.log(`  - Tiempo desde último cambio: ${tiempoDesdeUltimoCambio}ms`);
      console.log(`  - Status actual: "${prev.status}"`);
      console.log(`  - Status nuevo: "${datosPedido?.status}"`);
      console.log(`🔍 =================================`);
      console.log('📝 Actualizando infoPedido con datosPedido:', datosPedido);
      console.log('👥 numeroPersonas anterior:', prev.numeroPersonas);
      console.log('👥 numeroPersonas nuevo:', datosPedido?.numeroPersonas);

      const newState = {
        ...prev,
        idPedido: datosPedido?.idPedido || prev.idPedido,
        numeroPedido: datosPedido?.numeroPedido || prev.numeroPedido,
        onEditarCliente: onEditarCliente || null,
        // Actualizar mesa, mesero y numeroPersonas cuando cambien
        mesa: datosPedido?.mesa !== undefined ? datosPedido.mesa : prev.mesa,
        mesero: datosPedido?.mesero !== undefined ? datosPedido.mesero : prev.mesero,
        numeroPersonas: datosPedido?.numeroPersonas !== undefined ? datosPedido.numeroPersonas : prev.numeroPersonas,
        // Mantener callbacks de edición
        onEditarMesa: datosPedido?.onEditarMesa || prev.onEditarMesa,
        onEditarMesero: datosPedido?.onEditarMesero || prev.onEditarMesero,
        onEditarPersonas: datosPedido?.onEditarPersonas || prev.onEditarPersonas,
        ...datosPedido
      };

      // Solo actualizar el status si no lo hemos cambiado recientemente
      if (!preservarStatus && datosPedido?.status) {
        newState.status = datosPedido.status;
      }

      console.log('✅ infoPedido actualizado:', newState);
      console.log('✅ numeroPersonas final en infoPedido:', newState.numeroPersonas);
      return newState;
    });
  }, [datosPedido, onEditarCliente, ultimoCambioStatus]);
  const cargarDetallesPedido = async (idPedido) => {
    try {
      setLoading(true);
      console.log(`📋 CARGANDO DETALLE DE PEDIDO ID: ${idPedido}`);

      // Obtener el método correcto según el tipo de pedido
      const metodosAPI = obtenerMetodosAPI(tipoPedido);
      const response = await metodosAPI.obtener(idPedido);

      // El API responde con formato: { success: true, data: pedidoData }
      const pedidoCompleto = response;
      console.log('🔍 Pedido completo obtenido:', pedidoCompleto);

      // Extraer detalles del pedido según formato del API
      const detalles = pedidoCompleto.detalles || [];

      console.log(`📦 Detalles encontrados: ${detalles.length} productos`);
      detalles.forEach((item, index) => {
        console.log(`  ${index + 1}. ${item.nombrePlatillo} - Status: ${item.status} - Precio: $${item.precio}`);
      });

      // Convertir detalles al formato interno esperado
      const detallesFormateados = detalles.map(detalle => ({
        idPedidoDetalle: detalle.idPedidoDetalle,
        idProducto: detalle.idPlatillo,
        nombre: detalle.nombrePlatillo,
        precio: parseFloat(detalle.precio) || 0,
        cantidad: parseInt(detalle.cantidad) || 1,
        subtotal: (parseFloat(detalle.precio) || 0) * (parseInt(detalle.cantidad) || 1),
        observaciones: detalle.observaciones || [],
        // Determinar si es guardado basado en el status del API
        // 'impreso' = producto ya guardado y enviado a cocina
        // 'preparando' = producto nuevo que aún no se ha guardado
        status: detalle.status === 'impreso' ? 'guardado' : 'nuevo',
        impreso: detalle.status === 'impreso'
      }));

      // Separar productos guardados vs nuevos
      // Guardados: productos con status 'guardado' (que originalmente eran 'impreso')
      // Nuevos: productos con status 'nuevo' (que originalmente eran 'preparando')
      const guardados = detallesFormateados.filter(item => item.status === 'guardado');
      const nuevos = detallesFormateados.filter(item => item.status === 'nuevo');

      console.log(`✅ Productos separados:`);
      console.log(`  🆕 Nuevos: ${nuevos.length}`);
      console.log(`  💾 Guardados: ${guardados.length}`);

      // Actualizar estados
      setProductosNuevos(nuevos);
      setProductosGuardados(guardados);
      setPedidoActual(detallesFormateados);

      // Actualizar información del pedido
      setInfoPedido(prev => ({
        ...prev,
        idPedido: pedidoCompleto.idPedido || idPedido, // ¡CRÍTICO! Asegurar que se mantiene el idPedido
        numeroPedido: pedidoCompleto.numero || prev.numeroPedido,
        numeroPersonas: pedidoCompleto.numeroPersonas || prev.numeroPersonas,
        mesa: pedidoCompleto.mesa || prev.mesa,
        mesero: pedidoCompleto.mesero || prev.mesero,
        // Agregar campos directos para facilitar acceso
        nombreMesero: pedidoCompleto.nombreMesero || pedidoCompleto.mesero?.nombre || prev.nombreMesero,
        idMesero: pedidoCompleto.idMesero || pedidoCompleto.mesero?.idMesero || prev.idMesero,
        nombreMesa: pedidoCompleto.nombreMesa || pedidoCompleto.mesa?.caption || pedidoCompleto.mesa?.nombre || prev.nombreMesa,
        numeroMesa: pedidoCompleto.numeroMesa || pedidoCompleto.mesa?.numero || prev.numeroMesa,
        fechaCreacion: pedidoCompleto.fechaCreacion || prev.fechaCreacion,
        status: pedidoCompleto.status || prev.status
      }));

      console.log('✅ infoPedido actualizado con idPedido:', pedidoCompleto.idPedido || idPedido);

      // Si es pedido para llevar, verificar si tiene promoción de mesa activa
      if (tipoPedido === 'llevar') {
        const valorPromo = pedidoCompleto.promocionMesaActiva;
        const promoActiva = valorPromo === 1 || valorPromo === '1' || valorPromo === true || parseInt(valorPromo) === 1;
        console.log(`🏷️ ====== CARGA PROMOCION MESA ======`);
        console.log(`🏷️ pedidoCompleto keys:`, Object.keys(pedidoCompleto));
        console.log(`🏷️ Valor promocionMesaActiva del servidor: "${valorPromo}" (tipo: ${typeof valorPromo})`);
        console.log(`🏷️ promoActiva evaluado: ${promoActiva}`);
        console.log(`🏷️ ===================================`);

        setPromocionMesaActiva(promoActiva);

        // Si tiene promoción activa, cargar las promociones de mesa y obtener precios originales
        if (promoActiva) {
          console.log('🏷️ Cargando promociones de mesa y precios originales...');
          await cargarPromociones(true);

          // Obtener precios originales de la tabla platillo para cada producto guardado
          // Esto es necesario porque los precios en BD ya están con descuento
          try {
            const productosConPrecioOriginal = await Promise.all(
              guardados.map(async (producto) => {
                // Intentar obtener precio original de la API
                const idPlatillo = producto.idProducto || producto.idPlatillo;
                try {
                  const infoPlatillo = await API.categorias.getPlatillo(idPlatillo);
                  if (infoPlatillo && infoPlatillo.precio) {
                    return {
                      ...producto,
                      precioOriginal: parseFloat(infoPlatillo.precio)
                    };
                  }
                } catch (e) {
                  console.log(`⚠️ No se pudo obtener precio original para platillo ${idPlatillo}`);
                }
                return producto;
              })
            );

            // Actualizar productos guardados con precios originales
            setProductosGuardados(productosConPrecioOriginal);
            console.log('✅ Precios originales cargados para productos guardados');
          } catch (error) {
            console.error('Error al obtener precios originales:', error);
          }
        }
      }

      // Recalcular totales y promociones con todos los productos
      console.log('🔄 Recalculando totales y promociones...');
      await recalcularTotalesYPromociones(detallesFormateados);

      console.log('✅ Detalle de pedido cargado exitosamente');
      toast.success(`Pedido cargado1: ${detallesFormateados.length} productos (${guardados.length} guardados, ${nuevos.length} nuevos)`);

    } catch (error) {
      console.error("❌ Error al cargar detalles del pedido:", error);
      toast.error("Error al cargar los detalles del pedido: " + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };
  // Cargar categorías al iniciar
  useEffect(() => {
    const inicializarComponente = async () => {
      try {
        // Cargar datos iniciales en paralelo
        await Promise.all([
          cargarCategorias(),
          cargarPromociones(),
          cargarInfoNegocio()
        ]);

        // Una vez que las promociones están cargadas, cargar detalles del pedido si existe
        console.log("🔍 DEBUG infoPedido completo:", infoPedido);
        console.log("🔍 DEBUG infoPedido.pedidoExistente:", infoPedido.pedidoExistente);
        console.log("🔍 DEBUG infoPedido.idPedido:", infoPedido.idPedido);

        if (infoPedido.pedidoExistente?.idPedido || infoPedido.idPedido) {
          const idPedidoCargar = infoPedido.pedidoExistente?.idPedido || infoPedido.idPedido;
          console.log("📋 Pedido existente detectado, ID:", idPedidoCargar);

          // Si ya tenemos los detalles completos en pedidoExistente, usarlos directamente
          if (infoPedido.pedidoExistente && infoPedido.pedidoExistente.detalles) {
            console.log("📦 Usando detalles completos del pedidoExistente");
            const detalles = infoPedido.pedidoExistente.detalles || [];
            console.log("📦 Detalles encontrados:", detalles.length, detalles);
            console.log("📦 DEBUGGING - Detalles RAW:", JSON.stringify(detalles, null, 2));

            // Convertir detalles al formato interno esperado
            const detallesFormateados = detalles.map(detalle => ({
              idPedidoDetalle: detalle.idPedidoDetalle,
              idProducto: detalle.idPlatillo,
              nombre: detalle.nombrePlatillo,
              precio: parseFloat(detalle.precio) || 0,
              cantidad: parseInt(detalle.cantidad) || 1,
              subtotal: (parseFloat(detalle.precio) || 0) * (parseInt(detalle.cantidad) || 1),
              observaciones: detalle.observaciones || [],
              instrucciones: detalle.instrucciones || '',
              status: detalle.status || 'guardado',
              idCategoria: detalle.idCategoria || 0,
              nombreCategoria: detalle.nombreCategoria || ''
            }));

            console.log("📦 DEBUGGING - Detalles formateados:", detallesFormateados.length);
            console.log("📦 DEBUGGING - Detalles formateados completos:", JSON.stringify(detallesFormateados, null, 2));

            // DEBUG TEMPORAL - Ver status de productos
            console.log('🔍 DEBUG PRODUCTOS STATUS:');
            detallesFormateados.forEach((producto, index) => {
              console.log(`  Producto ${index + 1}: ${producto.nombre} - Status: "${producto.status}"`);
            });

            // Separar productos guardados de nuevos
            const productosGuardadosDesdeAPI = detallesFormateados.filter(item =>
              item.status === 'guardado' || item.status === 'impreso'
            );
            const productosNuevosDesdeAPI = detallesFormateados.filter(item =>
              item.status === 'nuevo' || item.status === 'pendiente'
            );

            console.log(`🔍 Productos guardados filtrados: ${productosGuardadosDesdeAPI.length}`);
            console.log(`🔍 Productos nuevos filtrados: ${productosNuevosDesdeAPI.length}`);

            setProductosGuardados(productosGuardadosDesdeAPI);
            setProductosNuevos(productosNuevosDesdeAPI);

            // Si es pedido para llevar, verificar si tiene promoción de mesa activa
            if (tipoPedido === 'llevar') {
              const pedidoData = infoPedido.pedidoExistente;
              const valorPromo = pedidoData.promocionMesaActiva;
              const promoActiva = valorPromo === 1 || valorPromo === '1' || valorPromo === true || parseInt(valorPromo) === 1;
              console.log(`🏷️ ====== CARGA PROMOCION MESA (desde pedidoExistente) ======`);
              console.log(`🏷️ pedidoExistente keys:`, Object.keys(pedidoData));
              console.log(`🏷️ Valor promocionMesaActiva: "${valorPromo}" (tipo: ${typeof valorPromo})`);
              console.log(`🏷️ promoActiva evaluado: ${promoActiva}`);
              console.log(`🏷️ ============================================================`);

              setPromocionMesaActiva(promoActiva);

              // Si tiene promoción activa, cargar las promociones de mesa y obtener precios originales
              if (promoActiva) {
                console.log('🏷️ Cargando promociones de mesa...');
                await cargarPromociones(true);

                // Obtener precios originales de la tabla platillo para cada producto guardado
                try {
                  const productosConPrecioOriginal = await Promise.all(
                    productosGuardadosDesdeAPI.map(async (producto) => {
                      const idPlatillo = producto.idProducto || producto.idPlatillo;
                      try {
                        const infoPlatillo = await API.categorias.getPlatillo(idPlatillo);
                        if (infoPlatillo && infoPlatillo.precio) {
                          return {
                            ...producto,
                            precioOriginal: parseFloat(infoPlatillo.precio)
                          };
                        }
                      } catch (e) {
                        console.log(`⚠️ No se pudo obtener precio original para platillo ${idPlatillo}`);
                      }
                      return producto;
                    })
                  );
                  setProductosGuardados(productosConPrecioOriginal);
                  console.log('✅ Precios originales cargados');
                } catch (error) {
                  console.error('Error al obtener precios originales:', error);
                }
              }
            }

            // Recalcular totales y promociones
            const todosLosProductos = [...productosGuardadosDesdeAPI, ...productosNuevosDesdeAPI];
            await recalcularTotalesYPromociones(todosLosProductos);
          } else {
            // Si no tenemos detalles completos, cargar desde API
            await cargarDetallesPedido(idPedidoCargar);
          }
        }
      } catch (error) {
        console.error("❌ Error al inicializar componente:", error);
      }
    };

    inicializarComponente();
  }, []);

  // Recalcular promociones cuando cambien las promociones disponibles
  useEffect(() => {
    // Solo recalcular si hay productos en el pedido y promociones disponibles
    // y evitar el recálculo inicial (cuando promociones se cargan por primera vez vacías)
    if (promociones.length > 0 && pedidoActual.length > 0) {
      console.log('🔄 Promociones actualizadas, recalculando...');
      // Crear una función inline para evitar dependencias circulares
      const recalcularConPromociones = async () => {
        try {
          setCalculandoPromociones(true);
          const promocionesCalculadas = await PromocionesService.calcularPromociones(pedidoActual, promociones);
          setPromocionesAplicadas(promocionesCalculadas);

          const subtotal = pedidoActual.reduce((total, item) => total + item.subtotal, 0);
          const descuentos = promocionesCalculadas.reduce((total, promo) => total + promo.descuento, 0);
          const total = subtotal - descuentos;

          setInfoPedido(prev => ({
            ...prev,
            subtotal,
            descuentos,
            total
          }));
        } catch (error) {
          console.error("❌ Error al recalcular promociones:", error);
        } finally {
          setCalculandoPromociones(false);
        }
      };

      recalcularConPromociones();
    }
  }, [promociones, pedidoActual]); // Dependencias necesarias

  // Función para recalcular totales y promociones usando el servicio centralizado
  const recalcularTotalesYPromociones = useCallback(async (productos) => {
    console.log('🔄 INICIANDO CÁLCULO DE PROMOCIONES CON SERVICIO CENTRALIZADO');
    console.log('Productos:', productos);
    console.log('Promociones disponibles:', promociones);

    setCalculandoPromociones(true);

    try {
      // Verificar si las promociones están cargadas, si no, cargarlas
      let promocionesParaUsar = promociones;
      if (!promocionesParaUsar || promocionesParaUsar.length === 0) {
        console.log('⚠️ Promociones vacías, recargando...');
        await cargarPromociones();
        // Las promociones se actualizan en el estado, pero necesitamos usar la versión más reciente
        const promocionesActualizadas = await API.promociones.getActivas();

        // Determinar qué tipo de promociones usar
        // Si promocionMesaActiva es true y es pedido para llevar, usar promociones de mesa
        const tipoPromocionAUsar = (tipoPedido === 'llevar' && promocionMesaActiva) ? 'mesa' : tipoPedido;

        // Filtrar promociones según el tipo de pedido
        promocionesParaUsar = (promocionesActualizadas || []).filter(promocion => {
          switch (tipoPromocionAUsar) {
            case 'mesa':
              return promocion.aplicaMesa === true || promocion.aplicaMesa === 1;
            case 'llevar':
              return promocion.aplicaLlevar === true || promocion.aplicaLlevar === 1;
            case 'domicilio':
              return promocion.aplicaDomicilio === true || promocion.aplicaDomicilio === 1;
            case 'uber':
              return promocion.aplicaUber === true || promocion.aplicaUber === 1;
            case 'rappi':
              return promocion.aplicaRappi === true || promocion.aplicaRappi === 1;
            case 'delibot':
              return promocion.aplicaDelibot === true || promocion.aplicaDelibot === 1;
            default:
              return false;
          }
        });

        setPromociones(promocionesParaUsar);
        console.log(`✅ Promociones recargadas y filtradas para ${tipoPedido}:`, promocionesParaUsar.length);
      }

      // Usar el servicio centralizado de promociones
      const promocionesCalculadas = await PromocionesService.calcularPromociones(productos, promocionesParaUsar);

      console.log('🎯 PROMOCIONES FINALES APLICADAS:', promocionesCalculadas);
      setPromocionesAplicadas(promocionesCalculadas);

      // IMPORTANTE: Actualizar pedidoActual con los productos recibidos
      setPedidoActual(productos);

      // Recalcular totales con promociones
      const subtotal = productos.reduce((total, item) => total + item.subtotal, 0);
      const descuentos = promocionesCalculadas.reduce((total, promo) => total + promo.descuento, 0);
      const total = subtotal - descuentos;

      console.log(`💰 TOTALES - Subtotal: $${subtotal}, Descuentos: $${descuentos}, Total: $${total}`);

      setInfoPedido(prev => ({
        ...prev,
        subtotal,
        descuentos,
        total
      }));
    } catch (error) {
      console.error("❌ Error al calcular promociones:", error);
    } finally {
      setCalculandoPromociones(false);
    }
  }, [promociones, tipoPedido]);

  // Función para obtener el método API correcto según el tipo de pedido
  const obtenerMetodosAPI = useCallback((tipo) => {
    const metodos = {
      mesa: {
        crear: API.pedidos.crearPedido,
        obtener: API.pedidos.getPedido,
        actualizar: API.pedidos.actualizarPedido || API.pedidos.crearPedido,
        agregarDetalles: API.pedidos.agregarProducto,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.cerrarPedido,
        abrirCuenta: API.pedidos.abrirCuenta,
        cerrar: API.pedidos.finalizarPedido
      },
      llevar: {
        crear: API.pedidos.crearPedidoLlevar,
        obtener: API.pedidos.getPedidoLlevar,
        actualizar: API.pedidos.actualizarPedidoLlevar,
        agregarDetalles: API.pedidos.agregarProductoLlevar,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.cerrarPedidoLlevar,
        abrirCuenta: API.pedidos.abrirCuentaLlevar,
        cerrar: API.pedidos.finalizarPedidoLlevar
      },
      domicilio: {
        crear: API.pedidos.crearPedidoDomicilio,
        obtener: API.pedidos.getPedidoDomicilio,
        actualizar: API.pedidos.actualizarPedidoDomicilio,
        agregarDetalles: API.pedidos.agregarProductoPedidoDomicilio,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.finalizarPedidoDomicilio,  // Cambio: usar finalizar en lugar de cerrar
        abrirCuenta: API.pedidos.abrirCuentaDomicilio,
        cerrar: API.pedidos.finalizarPedidoDomicilio
      },
      uber: {
        crear: API.pedidos.crearPedidoUber,
        obtener: API.pedidos.getPedidoUber,
        actualizar: API.pedidos.actualizarPedidoUber,
        agregarDetalles: API.pedidos.agregarProductoPedidoUber,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.cerrarPedidoUber,
        abrirCuenta: API.pedidos.abrirCuentaUber,
        cerrar: API.pedidos.finalizarPedidoUber
      },
      rappi: {
        crear: API.pedidos.crearPedidoRappi,
        obtener: API.pedidos.getPedidoRappi,
        actualizar: API.pedidos.actualizarPedidoRappi,
        agregarDetalles: API.pedidos.agregarProductoPedidoRappi,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.cerrarPedidoRappi,
        abrirCuenta: API.pedidos.abrirCuentaRappi,
        cerrar: API.pedidos.finalizarPedidoRappi
      },
      delibot: {
        crear: API.pedidos.crearPedidoDelibot,
        obtener: API.pedidos.getPedidoDelibot,
        actualizar: API.pedidos.actualizarPedidoDelibot,
        agregarDetalles: API.pedidos.agregarProductoPedidoDelibot,
        eliminarDetalle: API.pedidos.eliminarProducto,
        cerrarPedido: API.pedidos.cerrarPedidoDelibot,
        abrirCuenta: API.pedidos.abrirCuentaDelibot,
        cerrar: API.pedidos.finalizarPedidoDelibot
      }
    };

    return metodos[tipo] || metodos.mesa;
  }, []);

  // Función para actualizar el pedido de forma inteligente
  const actualizarPedidoInteligente = useCallback(async (esAutomatica = false) => {
    if (!infoPedido.idPedido) {
      console.log('❌ No hay ID de pedido para actualizar');
      return false;
    }

    try {
      if (esAutomatica) {
        setActualizandoEnSegundoPlano(true);
        console.log('🔄 Verificando cambios en segundo plano...');
      } else {
        setLoading(true);
        console.log('🔄 Actualizando pedido manualmente...');
      }

      // Obtener los datos actuales del servidor
      const metodosAPI = obtenerMetodosAPI(tipoPedido);
      const response = await metodosAPI.obtener(infoPedido.idPedido);
      const pedidoServidor = response;

      // Si el status del servidor es diferente al local, actualizar el status local
      // Solo si no hemos cambiado el status recientemente (menos de 30 segundos)
      const ahora = new Date();
      const tiempoEsperaStatus = ultimoCambioStatus ? (ahora - ultimoCambioStatus) : Infinity;

      if (pedidoServidor.status &&
        pedidoServidor.status !== infoPedido.status &&
        (tiempoEsperaStatus > 30000 || !esAutomatica)) { // 30 segundos o actualización manual

        console.log(`📊 ===== INTENTO ACTUALIZAR STATUS =====`);
        console.log(`  - Status local: "${infoPedido.status}"`);
        console.log(`  - Status servidor: "${pedidoServidor.status}"`);
        console.log(`  - Tiempo desde último cambio: ${tiempoEsperaStatus}ms`);
        console.log(`  - Es automática: ${esAutomatica}`);
        console.log(`  - ¿Puede actualizar?: ${tiempoEsperaStatus > 30000 || !esAutomatica}`);

        // PROTECCIÓN ADICIONAL: No sobrescribir si acabamos de finalizar
        if (infoPedido.status === 'pendiente' && pedidoServidor.status === 'abierto') {
          console.log(`🚫 BLOQUEANDO actualización status - pedido finalizado recientemente`);
        } else {
          console.log(`✅ Actualizando status: ${infoPedido.status} → ${pedidoServidor.status}`);
          setInfoPedido(prev => ({
            ...prev,
            status: pedidoServidor.status
          }));
        }
        console.log(`📊 ===================================`);
      }

      // Extraer detalles del servidor
      const detallesServidor = pedidoServidor.detalles || [];

      let hayChangios = false;

      // Para actualización automática, solo comparar productos guardados
      if (esAutomatica) {
        // Filtrar solo productos guardados del servidor (status = 'impreso')
        const productosGuardadosServidor = detallesServidor
          .filter(detalle => detalle.status === 'impreso')
          .map(detalle => ({
            idPedidoDetalle: detalle.idPedidoDetalle,
            idProducto: detalle.idPlatillo,
            nombre: detalle.nombrePlatillo,
            precio: parseFloat(detalle.precio) || 0,
            cantidad: parseInt(detalle.cantidad) || 1,
            subtotal: (parseFloat(detalle.precio) || 0) * (parseInt(detalle.cantidad) || 1),
            observaciones: detalle.observaciones || [],
            status: 'guardado',
            impreso: true
          }));

        // Comparar con productos guardados actuales
        const productosActualesIds = productosGuardados.map(p => p.idPedidoDetalle).sort();
        const productosServidorIds = productosGuardadosServidor.map(p => p.idPedidoDetalle).sort();

        hayChangios = JSON.stringify(productosActualesIds) !== JSON.stringify(productosServidorIds);

        if (!hayChangios) {
          console.log('✅ No hay cambios en productos guardados, manteniendo estado actual');
          setUltimaActualizacion(new Date());
          return true;
        }

        console.log('🔄 Detectados cambios en productos guardados, actualizando...');

        // Solo actualizar productos guardados, mantener los nuevos
        setProductosGuardados(productosGuardadosServidor);

        // Actualizar pedido actual combinando guardados del servidor + nuevos locales
        const pedidoActualizado = [...productosGuardadosServidor, ...productosNuevos];
        setPedidoActual(pedidoActualizado);

        // Recalcular promociones
        await recalcularTotalesYPromociones(pedidoActualizado);

      } else {
        // Para actualización manual, recargar todo como antes
        await cargarDetallesPedido(infoPedido.idPedido);
        hayChangios = true; // Para actualización manual siempre mostrar mensaje
      }

      setUltimaActualizacion(new Date());

      if (!esAutomatica) {
        toast.success('Pedido actualizado correctamente');
      } else if (hayChangios) {
        console.log('✅ Productos guardados sincronizados desde el servidor');
      }

      return true;
    } catch (error) {
      console.error('❌ Error al actualizar pedido:', error);
      if (!esAutomatica) {
        toast.error('Error al actualizar el pedido');
      }
      return false;
    } finally {
      if (esAutomatica) {
        setActualizandoEnSegundoPlano(false);
      } else {
        setLoading(false);
      }
    }
  }, [infoPedido.idPedido, tipoPedido, obtenerMetodosAPI, productosGuardados, productosNuevos, cargarDetallesPedido, recalcularTotalesYPromociones, toast, ultimoCambioStatus]);

  // Efecto para actualización automática cada 10 segundos - DESACTIVADO TEMPORALMENTE
  /*
  useEffect(() => {
    // Solo activar actualización automática si hay un pedido existente
    if (!infoPedido.idPedido) {
      return;
    }

    console.log('⏰ Iniciando actualización automática cada 10 segundos');

    const intervalo = setInterval(async () => {
      console.log('🔄 Ejecutando actualización automática...');
      await actualizarPedidoInteligente(true);
    }, 10000); // 10 segundos

    // Cleanup del intervalo
    return () => {
      console.log('⏰ Limpiando intervalo de actualización automática');
      clearInterval(intervalo);
    };
  }, [infoPedido.idPedido, actualizarPedidoInteligente]);
  */


  // Función para cargar categorías
  const cargarCategorias = async () => {
    try {
      setLoading(true);
      const categoriasData = await API.categorias.getAll();
      setCategorias(categoriasData);
    } catch (error) {
      console.error("Error al cargar categorías:", error);
      toast.error("Error al cargar las categorías");
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar productos de una categoría
  const cargarProductosCategoria = useCallback(async (categoria) => {
    try {
      setLoading(true);
      const productosData = await API.productos.getPorCategoria(categoria.idCategoria);

      const esDelivery = ['rappi', 'uber', 'delibot'].includes(tipoPedido);

      // Leer precios delivery configurados por producto y plataforma
      let preciosDelivery = {};
      try {
        preciosDelivery = JSON.parse(localStorage.getItem('delivery_precios_platillos') || '{}');
      } catch { /* usa objeto vacío */ }

      const productosProcessados = productosData.map(producto => {
        if (esDelivery) {
          // getPorCategoria devuelve idProducto (alias de idPlatillo en la BD)
          const idKey = producto.idProducto ?? producto.idPlatillo;
          const configProducto = preciosDelivery[idKey];
          const configPlat = configProducto?.[tipoPedido];
          let precioDelivery;
          if (configPlat?.precio > 0) {
            precioDelivery = configPlat.precio;
          } else {
            const pct = configPlat?.pct ?? 30;
            precioDelivery = Math.round((producto.precio * (1 + pct / 100)) * 100) / 100;
          }
          return {
            ...producto,
            precioOriginal: producto.precio,
            precio: precioDelivery
          };
        } else {
          return {
            ...producto,
            precioOriginal: producto.precio,
            precio: producto.precio
          };
        }
      });

      setProductos(productosProcessados);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  }, [toast, tipoPedido]);

  // Función para cargar promociones disponibles
  const cargarPromociones = async (usarPromoMesa = false) => {
    try {
      const promocionesData = await API.promociones.getActivas();

      // Determinar qué tipo de promociones usar
      // Si usarPromoMesa es true y es pedido para llevar, usar promociones de mesa
      const tipoPromocionAUsar = (tipoPedido === 'llevar' && usarPromoMesa) ? 'mesa' : tipoPedido;

      // Filtrar promociones según el tipo de pedido
      const promocionesFiltradas = (promocionesData || []).filter(promocion => {
        switch (tipoPromocionAUsar) {
          case 'mesa':
            return promocion.aplicaMesa === true || promocion.aplicaMesa === 1;
          case 'llevar':
            return promocion.aplicaLlevar === true || promocion.aplicaLlevar === 1;
          case 'domicilio':
            return promocion.aplicaDomicilio === true || promocion.aplicaDomicilio === 1;
          case 'uber':
            return promocion.aplicaUber === true || promocion.aplicaUber === 1;
          case 'rappi':
            return promocion.aplicaRappi === true || promocion.aplicaRappi === 1;
          case 'delibot':
            return promocion.aplicaDelibot === true || promocion.aplicaDelibot === 1;
          default:
            return false;
        }
      });

      console.log(`✅ Promociones filtradas para ${tipoPromocionAUsar} (original: ${tipoPedido}, usarPromoMesa: ${usarPromoMesa}):`, promocionesFiltradas.length, 'de', promocionesData?.length || 0);
      setPromociones(promocionesFiltradas);
      return promocionesFiltradas;
    } catch (error) {
      console.error("Error al cargar promociones:", error);
      return [];
    }
  };

  // Handler para toggle de promoción de mesa en pedidos para llevar
  // SOLO actualiza precios en BD, nunca agrega productos
  const handleTogglePromocionMesa = useCallback(async () => {
    if (tipoPedido !== 'llevar') return;
    if (!infoPedido.idPedido) {
      toast.warning('Primero guarda el pedido antes de aplicar promociones de mesa');
      return;
    }
    if (productosGuardados.length === 0) {
      toast.warning('No hay productos guardados para aplicar promociones');
      return;
    }

    const nuevoEstado = !promocionMesaActiva;
    console.log(`🏷️ Toggle promoción de mesa: ${nuevoEstado ? 'ACTIVADO' : 'DESACTIVADO'}`);
    console.log('📋 Productos guardados:', productosGuardados);

    setCalculandoPromociones(true);

    try {
      if (nuevoEstado) {
        // ACTIVAR: Calcular descuentos y actualizar precios en BD
        const promocionesNuevas = await cargarPromociones(true);
        console.log('📋 Promociones de mesa cargadas:', promocionesNuevas);

        if (promocionesNuevas.length === 0) {
          toast.warning('No hay promociones de mesa activas');
          setCalculandoPromociones(false);
          return;
        }

        // Calcular promociones para los productos guardados
        const promocionesCalculadas = await PromocionesService.calcularPromociones(productosGuardados, promocionesNuevas);
        console.log('📋 Promociones calculadas:', promocionesCalculadas);

        // Preparar lista de productos con nuevo precio
        const productosConDescuento = [];

        // Procesar cada promoción calculada
        promocionesCalculadas.forEach(promo => {
          console.log(`📋 Procesando promoción tipo ${promo.tipo}:`, promo);

          if (promo.tipo === '1' || promo.tipo === 'porcentaje') {
            // TIPO 1: Descuento por porcentaje
            const porcentaje = promo.porcentajeDescuento || 0;
            const productosAAplicar = promo.productosIncluidos || [];

            productosAAplicar.forEach(idPedidoDetalle => {
              const producto = productosGuardados.find(p =>
                p.idPedidoDetalle == idPedidoDetalle
              );
              if (producto && porcentaje > 0) {
                productosConDescuento.push({
                  idPedidoDetalle: parseInt(producto.idPedidoDetalle),
                  nuevoPrecio: parseFloat((producto.precio * (1 - porcentaje / 100)).toFixed(2)),
                  precioOriginal: producto.precio
                });
              }
            });

          } else if (promo.tipo === '2' || promo.tipo === '2x1') {
            // TIPO 2: 2x1 - Los productos gratis van a precio 0
            const productosGratis = promo.productosGratis || promo.productosGratisDetalle || [];
            console.log('📋 Productos gratis (2x1):', productosGratis);

            productosGratis.forEach(item => {
              const idPedidoDetalle = typeof item === 'object' ? item.idPedidoDetalle : item;
              const producto = productosGuardados.find(p =>
                p.idPedidoDetalle == idPedidoDetalle
              );
              if (producto) {
                productosConDescuento.push({
                  idPedidoDetalle: parseInt(producto.idPedidoDetalle),
                  nuevoPrecio: 0,
                  precioOriginal: producto.precio
                });
              }
            });

          } else if (promo.tipo === '3' || promo.tipo === 'combo') {
            // TIPO 3: Combo - Los productos gratis van a precio 0
            const productosGratis = promo.productosGratis || promo.productosGratisDetalle || [];

            productosGratis.forEach(item => {
              const idPedidoDetalle = typeof item === 'object' ? item.idPedidoDetalle : item;
              const producto = productosGuardados.find(p =>
                p.idPedidoDetalle == idPedidoDetalle
              );
              if (producto) {
                productosConDescuento.push({
                  idPedidoDetalle: parseInt(producto.idPedidoDetalle),
                  nuevoPrecio: 0,
                  precioOriginal: producto.precio
                });
              }
            });
          }
        });

        console.log('📋 Productos con descuento a enviar:', productosConDescuento);

        if (productosConDescuento.length > 0) {
          console.log('📦 Llamando API para actualizar precios...');
          await API.pedidos.actualizarPreciosConPromocion(
            infoPedido.idPedido,
            true,
            productosConDescuento
          );
          console.log('✅ Precios actualizados en BD y campo promocionMesaActiva = 1');

          // ACTUALIZAR INTERFAZ: Crear mapa de nuevos precios
          const mapaPrecios = new Map();
          productosConDescuento.forEach(p => {
            mapaPrecios.set(p.idPedidoDetalle, {
              nuevoPrecio: p.nuevoPrecio,
              precioOriginal: p.precioOriginal
            });
          });

          // Actualizar estado local de productos guardados CON SUBTOTAL
          const productosActualizados = productosGuardados.map(producto => {
            const precioInfo = mapaPrecios.get(parseInt(producto.idPedidoDetalle));
            if (precioInfo) {
              const nuevoPrecio = precioInfo.nuevoPrecio;
              return {
                ...producto,
                precioOriginal: precioInfo.precioOriginal,
                precio: nuevoPrecio,
                subtotal: nuevoPrecio * producto.cantidad // Recalcular subtotal
              };
            }
            return producto;
          });

          setProductosGuardados(productosActualizados);

          // ACTUALIZAR pedidoActual para que los totales se recalculen
          const todosLosProductos = [...productosActualizados, ...productosNuevos];
          setPedidoActual(todosLosProductos);

          console.log('✅ Interfaz actualizada con precios de promoción');
          toast.success('Promociones de mesa aplicadas');

          setPromocionesAplicadas(promocionesCalculadas);
          setPromocionMesaActiva(true);
        } else {
          // NO hay productos con descuento - NO activar el toggle
          console.log('⚠️ No hay productos con descuento para actualizar');
          toast.info('No se encontraron promociones aplicables a estos productos');
          setCalculandoPromociones(false);
          return; // Salir sin activar el toggle
        }

      } else {
        // DESACTIVAR: Restaurar precios originales desde tabla platillo
        console.log('📦 Llamando API para restaurar precios originales...');
        const response = await API.pedidos.actualizarPreciosConPromocion(
          infoPedido.idPedido,
          false
        );
        console.log('✅ Precios restaurados en BD:', response);

        // ACTUALIZAR INTERFAZ: Restaurar precios originales
        // Si tenemos precioOriginal guardado, usarlo; si no, obtener de la respuesta de la API
        const preciosRestaurados = response?.data?.preciosRestaurados || [];
        const mapaPreciosRestaurados = new Map();
        preciosRestaurados.forEach(p => {
          mapaPreciosRestaurados.set(parseInt(p.idPedidoDetalle), parseFloat(p.precio));
        });

        // Actualizar productos CON SUBTOTAL RECALCULADO
        const productosActualizados = productosGuardados.map(producto => {
          let precioRestaurado = producto.precio;

          // Primero intentar usar precioOriginal guardado localmente
          if (producto.precioOriginal !== undefined && producto.precioOriginal !== null) {
            precioRestaurado = producto.precioOriginal;
          } else {
            // Si no hay precioOriginal local, usar el de la respuesta de la API
            const precioAPI = mapaPreciosRestaurados.get(parseInt(producto.idPedidoDetalle));
            if (precioAPI !== undefined) {
              precioRestaurado = precioAPI;
            }
          }

          // Eliminar precioOriginal y recalcular subtotal
          const { precioOriginal, ...productoSinOriginal } = producto;
          return {
            ...productoSinOriginal,
            precio: precioRestaurado,
            subtotal: precioRestaurado * producto.cantidad // Recalcular subtotal
          };
        });

        setProductosGuardados(productosActualizados);

        // ACTUALIZAR pedidoActual para que los totales se recalculen
        const todosLosProductos = [...productosActualizados, ...productosNuevos];
        setPedidoActual(todosLosProductos);

        console.log('✅ Interfaz actualizada con precios originales');

        await cargarPromociones(false);
        setPromocionesAplicadas([]);
        setPromocionMesaActiva(false);
        toast.success('Precios originales restaurados');
      }

    } catch (error) {
      console.error('Error al cambiar promociones:', error);
      toast.error('Error al cambiar las promociones: ' + error.message);
    } finally {
      setCalculandoPromociones(false);
    }
  }, [tipoPedido, promocionMesaActiva, productosGuardados, productosNuevos, infoPedido, toast, cargarPromociones]);

  // Función para cargar información del negocio
  const cargarInfoNegocio = async () => {
    try {
      const response = await API.infoNegocio.obtenerInfo();
      if (response.success && response.data) {
        setInfoNegocio(response.data);
        console.log("✅ Información del negocio cargada:", response.data);
      }
    } catch (error) {
      console.error("Error al cargar información del negocio:", error);
      // En caso de error, se mantienen los valores por defecto
    }
  };

  // Función para validar si una promoción aplica por fecha, hora y tipo de pedido
  // NOTA: Esta función ya no se usa, mantenida por compatibilidad
  const validarPromocionAplica = useCallback((promocion) => {
    const ahora = new Date();
    const fechaActual = ahora.toISOString().split('T')[0];
    const horaActual = ahora.toTimeString().split(' ')[0];

    // Validar fechas
    if (promocion.fechaInicio && fechaActual < promocion.fechaInicio) return false;
    if (promocion.fechaFin && fechaActual > promocion.fechaFin) return false;

    // Validar horas
    if (promocion.horaInicio && horaActual < promocion.horaInicio) return false;
    if (promocion.horaFin && horaActual > promocion.horaFin) return false;

    // Validar tipo de pedido
    switch (tipoPedido) {
      case 'mesa':
        return promocion.aplicaMesa;
      case 'llevar':
        return promocion.aplicaLlevar;
      case 'domicilio':
        return promocion.aplicaDomicilio;
      case 'uber':
        return promocion.aplicaUber;
      case 'rappi':
        return promocion.aplicaRappi;
      case 'delibot':
        return promocion.aplicaDelibot;
      default:
        return false;
    }
  }, [tipoPedido]);

  // Función para calcular promociones Tipo 1 (descuento por porcentaje)
  // NOTA: Esta función ya no se usa, el cálculo se hace en PromocionesService
  const calcularPromocionTipo1 = useCallback((promocion, productos) => {
    console.log('=== CALCULANDO PROMOCIÓN TIPO 1 ===');
    console.log('Promoción:', promocion.nombre, promocion);
    console.log('Productos en pedido:', productos);

    if (!promocion.aplicaProductos || promocion.aplicaProductos.length === 0) {
      console.log('❌ No hay productos aplicables en la promoción');
      return { descuento: 0, productosAfectados: [] };
    }

    const productosAplicables = productos.filter(producto => {
      const idProducto = producto.idProducto || producto.idPlatillo;
      const aplica = promocion.aplicaProductos.includes(idProducto);
      console.log(`Producto ${producto.nombre} (ID: ${idProducto}) - Aplica: ${aplica}`);
      return aplica;
    });

    console.log('Productos aplicables encontrados:', productosAplicables);

    if (productosAplicables.length === 0) {
      console.log('❌ No hay productos aplicables en el pedido');
      return { descuento: 0, productosAfectados: [] };
    }

    const subtotalAplicable = productosAplicables.reduce((total, item) => total + item.subtotal, 0);
    const porcentajeDescuento = parseFloat(promocion.descuento);
    const descuento = subtotalAplicable * porcentajeDescuento;

    console.log(`Subtotal aplicable: $${subtotalAplicable}`);
    console.log(`Porcentaje descuento: ${porcentajeDescuento * 100}%`);
    console.log(`Descuento calculado: $${descuento}`);

    const resultado = {
      descuento,
      productosAfectados: productosAplicables.map(p => p.idPedidoDetalle),
      tipoDescuento: 'porcentaje',
      porcentaje: porcentajeDescuento * 100
    };

    console.log('✅ Resultado final:', resultado);
    return resultado;
  }, []);

  // Función para generar e imprimir ticket final
  const generarEImprimirTicketFinal = useCallback(async () => {
    const tStart = performance.now();
    try {
      // Obtener código de sucursal — solo de caché, nunca esperar API
      const codigoSucursal = localStorage.getItem('_cached_codigo_sucursal') || 'N/A';
      // Refrescar caché en background para la próxima vez (no bloquea)
      API.configuracion.getCodigoSucursal().then(r => {
        if (r.success) localStorage.setItem('_cached_codigo_sucursal', r.data);
      }).catch(() => {});

      // Calcular totales incluyendo promociones
      // NOTA: pedidoActual ya incluye todos los productos (guardados + nuevos)
      // No necesitamos hacer spread de productosGuardados porque causaría duplicación
      const todosLosProductos = pedidoActual;
      const subtotal = todosLosProductos.reduce((sum, producto) =>
        sum + (producto.precio * producto.cantidad), 0);
      const descuentos = promocionesAplicadas.reduce((total, promo) => total + (promo.descuento || 0), 0);
      const total = subtotal - descuentos;

      // Obtener información del pedido
      const fechaActual = new Date();
      const fechaFormateada = fechaActual.toLocaleDateString('es-MX');
      const horaFormateada = fechaActual.toLocaleTimeString('es-MX');
      // Obtener numeroDia: primero del estado local, si no existe consultar al servidor
      let numeroDia = infoPedido.numeroDia || infoPedido.numero_dia || infoPedido.pedidoExistente?.numeroDia || infoPedido.pedidoExistente?.numero_dia || 0;
      if (!numeroDia && infoPedido.idPedido) {
        try {
          const pedidoServer = await API.pedidos.getPedido(infoPedido.idPedido);
          numeroDia = pedidoServer?.numeroDia || pedidoServer?.numero_dia || 0;
        } catch (e) {
          console.error('Error al obtener numeroDia del servidor:', e);
        }
      }

      // Generar contenido del ticket para impresora térmica 80mm (36 caracteres útiles + padding izquierdo)
      const anchoTicket = 36;
      const margenIzquierdo = '  '; // 3 espacios de padding izquierdo

      const centrarTexto = (texto, ancho = anchoTicket) => {
        const textoTruncado = texto.length > ancho ? texto.substring(0, ancho) : texto;
        const espacios = Math.max(0, Math.floor((ancho - textoTruncado.length) / 2));
        return margenIzquierdo + (' '.repeat(espacios) + textoTruncado).padEnd(ancho);
      };

      const conMargen = (texto) => margenIzquierdo + texto;

      // Helper: dividir texto largo en líneas y centrar cada una
      const centrarTextoLargo = (texto, ancho = anchoTicket) => {
        // Primero dividir por saltos de línea existentes
        const partes = texto.split(/\r?\n/);
        const resultado = [];
        partes.forEach(parte => {
          const parteTrimmed = parte.trim();
          if (!parteTrimmed) return;
          if (parteTrimmed.length <= ancho) {
            resultado.push(centrarTexto(parteTrimmed, ancho));
          } else {
            // Dividir por palabras si excede el ancho
            const palabras = parteTrimmed.split(' ');
            let lineaActual = '';
            palabras.forEach(palabra => {
              const nueva = lineaActual ? `${lineaActual} ${palabra}` : palabra;
              if (nueva.length <= ancho) {
                lineaActual = nueva;
              } else {
                if (lineaActual) resultado.push(centrarTexto(lineaActual, ancho));
                lineaActual = palabra;
              }
            });
            if (lineaActual) resultado.push(centrarTexto(lineaActual, ancho));
          }
        });
        return resultado;
      };

      // Construir encabezado dinámicamente según configuración
      const camposEncabezado = (() => {
        try { return JSON.parse(localStorage.getItem('ticket_campos_encabezado_cuenta') || '{}'); }
        catch { return {}; }
      })();
      const mostrarCampo = (campo) => camposEncabezado[campo] !== undefined ? camposEncabezado[campo] : true;

      const lineasEncabezado = [];
      if (mostrarCampo('nombre_negocio')) {
        const nombreCompleto = mostrarCampo('nombre_sucursal') && infoNegocio.nombre_sucursal
          ? `${infoNegocio.nombre_negocio} ${infoNegocio.nombre_sucursal}`
          : infoNegocio.nombre_negocio;
        lineasEncabezado.push(...centrarTextoLargo(nombreCompleto));
      } else if (mostrarCampo('nombre_sucursal') && infoNegocio.nombre_sucursal) {
        lineasEncabezado.push(...centrarTextoLargo(infoNegocio.nombre_sucursal));
      }
      if (mostrarCampo('direccion_linea1') && infoNegocio.direccion_linea1) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.direccion_linea1));
      if (mostrarCampo('direccion_linea2') && infoNegocio.direccion_linea2) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.direccion_linea2));
      if (mostrarCampo('razon_social') && infoNegocio.razon_social) lineasEncabezado.push(...centrarTextoLargo(infoNegocio.razon_social));
      if (mostrarCampo('rfc') && infoNegocio.rfc) lineasEncabezado.push(...centrarTextoLargo(`RFC: ${infoNegocio.rfc}`));
      if (mostrarCampo('telefono') && infoNegocio.telefono) lineasEncabezado.push(...centrarTextoLargo(`Tel: ${infoNegocio.telefono}`));

      const ticketContent = `${conMargen('='.repeat(anchoTicket))}
${lineasEncabezado.join('\n')}
${conMargen('='.repeat(anchoTicket))}
${centrarTexto('CUENTA DE CONSUMO')}
${centrarTexto(`OPERACION # ${infoPedido.idPedido || infoPedido.numeroPedido}`)}
${conMargen('='.repeat(anchoTicket))}
${conMargen('No CTA   : ' + numeroDia)}
${conMargen('FECHA    : ' + fechaFormateada)}
${conMargen('PRODUCTOS: ' + todosLosProductos.reduce((sum, p) => sum + p.cantidad, 0))}
${conMargen('ATENDIO  : ' + (infoPedido.mesero || infoPedido.mesero?.nombre || 'Sistema'))}
${tipoPedido === 'mesa' ? conMargen('MESA     : ' + (typeof infoPedido.mesa === 'object' ? (infoPedido.mesa?.caption || infoPedido.mesa?.numero || infoPedido.mesa?.nombre || infoPedido.mesa?.id || '') : infoPedido.mesa)) : conMargen('TIPO     : ' + tipoPedido.toUpperCase())}
${conMargen('HORA     : ' + horaFormateada)}
${(() => {
        if (tipoPedido === 'llevar') {
          const cliente = infoPedido.nombreCliente || infoPedido.infoCliente?.nombre;
          return cliente ? conMargen('CLIENTE  : ' + cliente) : '';
        }
        if (tipoPedido === 'uber' || tipoPedido === 'rappi' || tipoPedido === 'delibot') {
          const cliente = infoPedido.clienteLlevar || infoPedido.nombreCliente || infoPedido.infoCliente?.nombre;
          return cliente ? conMargen('CLIENTE  : ' + cliente) : '';
        }
        return '';
      })()}
${tipoPedido === 'domicilio' ? `${conMargen('='.repeat(anchoTicket))}
${centrarTexto('DATOS DEL CLIENTE')}
${conMargen('='.repeat(anchoTicket))}
${conMargen('NOMBRE   : ' + (infoPedido.nombreCliente || infoPedido.infoCliente?.nombre || 'No especificado'))}
${conMargen('TELEFONO : ' + (infoPedido.telefono || infoPedido.infoCliente?.telefono || 'No especificado'))}
${conMargen('DIRECCION: ' + (() => {
        const direccion = infoPedido.direccion || infoPedido.infoCliente?.direccion || 'No especificada';
        // Dividir dirección en líneas si es muy larga (36 - 11 label = 25 útiles)
        if (direccion.length <= 25) {
          return direccion;
        } else {
          const palabras = direccion.split(' ');
          const lineas = [];
          let lineaActual = '';

          palabras.forEach(palabra => {
            const nuevaLinea = lineaActual ? `${lineaActual} ${palabra}` : palabra;
            if (nuevaLinea.length <= 25) {
              lineaActual = nuevaLinea;
            } else {
              if (lineaActual) lineas.push(lineaActual);
              lineaActual = palabra;
            }
          });

          if (lineaActual) lineas.push(lineaActual);
          // Alinear líneas de continuación bajo el inicio de la dirección
          // (2 de margen izquierdo + 11 de la etiqueta "DIRECCION: " = 13)
          return lineas.join('\n' + ' '.repeat(13));
        }
      })())}${infoPedido.referencia ? `
${conMargen('REFERENCIA: ' + (() => {
        const referencia = infoPedido.referencia;
        if (referencia.length <= 24) {
          return referencia;
        } else {
          const palabras = referencia.split(' ');
          const lineas = [];
          let lineaActual = '';

          palabras.forEach(palabra => {
            const nuevaLinea = lineaActual ? `${lineaActual} ${palabra}` : palabra;
            if (nuevaLinea.length <= 24) {
              lineaActual = nuevaLinea;
            } else {
              if (lineaActual) lineas.push(lineaActual);
              lineaActual = palabra;
            }
          });

          if (lineaActual) lineas.push(lineaActual);
          // Alinear líneas de continuación bajo el inicio de la referencia
          // (2 de margen izquierdo + 12 de la etiqueta "REFERENCIA: " = 14)
          return lineas.join('\n' + ' '.repeat(14));
        }
      })())}` : ''}` : ''}
${conMargen('='.repeat(anchoTicket))}
${conMargen('PRODUCTO      CNT PRECIO  IMPORTE')}
${conMargen('='.repeat(anchoTicket))}
${(() => {
          // Agrupar productos por nombre + precio para reducir tamaño del ticket
          const productosAgrupados = [];
          todosLosProductos.forEach(producto => {
            const existente = productosAgrupados.find(p => p.nombre === producto.nombre && p.precio === producto.precio);
            if (existente) {
              existente.cantidad += producto.cantidad;
            } else {
              productosAgrupados.push({ nombre: producto.nombre, precio: producto.precio, cantidad: producto.cantidad });
            }
          });
          return productosAgrupados;
        })().map(producto => {
          const importe = producto.precio * producto.cantidad;
          const nombreCompleto = producto.nombre;
          // Ajustar anchos para impresora 80mm (ancho total 36)
          const anchoNombre = 13;    // PRODUCTO (13 chars)
          const anchoCantidad = 3;   // CNT (3 chars)
          const anchoPrecio = 7;     // PRECIO (7 chars)
          const anchoImporte = 8;    // IMPORTE (8 chars)
          // Total: 13 + 1 + 3 + 1 + 7 + 1 + 8 = 34 chars + margen

          // Función para dividir texto en líneas de ancho fijo
          const dividirTexto = (texto, ancho) => {
            const lineas = [];
            let textoRestante = texto;

            while (textoRestante.length > ancho) {
              // Buscar el último espacio antes del límite
              let corte = ancho;
              const ultimoEspacio = textoRestante.lastIndexOf(' ', ancho);

              if (ultimoEspacio > ancho * 0.6) { // Si hay espacio en el 60% final, usarlo
                corte = ultimoEspacio;
              }

              lineas.push(textoRestante.substring(0, corte));
              textoRestante = textoRestante.substring(corte).trim();
            }

            if (textoRestante.length > 0) {
              lineas.push(textoRestante);
            }

            return lineas;
          };

          const lineasNombre = dividirTexto(nombreCompleto, anchoNombre);

          // Primera línea con todos los datos y margen izquierdo
          let resultado = margenIzquierdo + `${lineasNombre[0].padEnd(anchoNombre)} ${producto.cantidad.toString().padStart(anchoCantidad)} ${producto.precio.toFixed(2).padStart(anchoPrecio)} ${importe.toFixed(2).padStart(anchoImporte)}`;

          // Líneas adicionales solo con el nombre y margen izquierdo
          for (let i = 1; i < lineasNombre.length; i++) {
            resultado += `\n${margenIzquierdo}${lineasNombre[i].padEnd(anchoNombre)}`;
          }

          return resultado;
        }).join('\n')}
${conMargen('-'.repeat(anchoTicket))}
${conMargen('SUBTOTAL:'.padEnd(anchoTicket - 10) + subtotal.toFixed(2).padStart(10))}
${descuentos > 0 ? conMargen('DESCUENTOS:'.padEnd(anchoTicket - 10) + ('-' + descuentos.toFixed(2)).padStart(10)) + '\n' : ''}${conMargen('-'.repeat(anchoTicket))}
${conMargen('TOTAL A PAGAR:'.padEnd(anchoTicket - 10) + total.toFixed(2).padStart(10))}
${conMargen('='.repeat(anchoTicket))}
${(() => {
          // Dividir y centrar el texto en líneas de máximo anchoTicket caracteres
          const textoCompleto = `${numeroALetras(total)} PESOS, ${((total % 1) * 100).toFixed(0).padStart(2, '0')}/100 M.N.`;
          const anchoMaximo = anchoTicket;
          const palabras = textoCompleto.split(' ');
          const lineas = [];
          let lineaActual = '';

          palabras.forEach(palabra => {
            const nuevaLinea = lineaActual ? `${lineaActual} ${palabra}` : palabra;
            if (nuevaLinea.length <= anchoMaximo) {
              lineaActual = nuevaLinea;
            } else {
              if (lineaActual) lineas.push(lineaActual);
              lineaActual = palabra;
            }
          });

          if (lineaActual) lineas.push(lineaActual);

          // Centrar cada línea
          return lineas.map(linea => centrarTexto(linea)).join('\n');
        })()}
${conMargen('='.repeat(anchoTicket))}
${(() => {
          // Leer líneas configurables del pie de ticket
          try {
            const configFin = JSON.parse(localStorage.getItem('ticket_fin_cuenta') || '{}');
            const lineasPie = [];
            if (configFin.linea1) lineasPie.push(...centrarTextoLargo(configFin.linea1));
            if (configFin.linea2) lineasPie.push(...centrarTextoLargo(configFin.linea2));
            if (configFin.linea3) lineasPie.push(...centrarTextoLargo(configFin.linea3));
            if (infoNegocio.mensaje_ticket) lineasPie.push(...centrarTextoLargo(infoNegocio.mensaje_ticket));
            if (lineasPie.length > 0) return '\n' + lineasPie.join('\n\n') + '\n';
            // Fallback si no hay nada configurado
            return '\n' + centrarTexto('>> Gracias Por Su Preferencia <<') + '\n\n' +
              centrarTexto('*** FAVOR DE PRESENTAR ESTA ***') + '\n' +
              centrarTexto('***   CUENTA PARA EL PAGO   ***') + '\n';
          } catch {
            return '\n' + centrarTexto('>> Gracias Por Su Preferencia <<') + '\n';
          }
        })()}
${(() => {
          // Obtener configuración del sistema desde localStorage
          try {
            const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');

            // Verificar si debe mostrar leyendas de facturación
            if (systemConfig.linkFactura === 1 || systemConfig.linkFactura === "1") {

              let leyendasTexto = [];

              // Siempre agregar separador inicial con margen
              leyendasTexto.push(conMargen('='.repeat(anchoTicket)));

              // Función para dividir texto (reutilizar la misma lógica)
              const dividirTexto = (texto, ancho) => {
                const lineas = [];
                let textoRestante = texto;

                while (textoRestante.length > ancho) {
                  // Buscar el último espacio antes del límite
                  let corte = ancho;
                  const ultimoEspacio = textoRestante.lastIndexOf(' ', ancho);

                  if (ultimoEspacio > ancho * 0.6) { // Si hay espacio en el 60% final, usarlo
                    corte = ultimoEspacio;
                  }

                  lineas.push(textoRestante.substring(0, corte));
                  textoRestante = textoRestante.substring(corte).trim();
                }

                if (textoRestante.length > 0) {
                  lineas.push(textoRestante);
                }

                return lineas;
              };

              // Agregar leyandaInicio si existe
              if (systemConfig.leyandaInicio && systemConfig.leyandaInicio.trim()) {
                const lineasInicio = systemConfig.leyandaInicio.split('\n');
                lineasInicio.forEach(linea => {
                  if (linea.trim()) {
                    // Dividir líneas largas respetando el ancho del ticket
                    const lineasDivididas = dividirTexto(linea.trim(), anchoTicket);
                    lineasDivididas.forEach(lineaDividida => {
                      leyendasTexto.push(centrarTexto(lineaDividida));
                    });
                  }
                });
              }

              // Agregar información de facturación entre leyendas si ambas existen
              if (systemConfig.leyandaInicio && systemConfig.leyandaInicio.trim() &&
                systemConfig.Leyenda && systemConfig.Leyenda.trim()) {

                // Separador con margen
                leyendasTexto.push(conMargen('='.repeat(anchoTicket)));

                // Generar código de facturación (temporal, se guardará en BD al cerrar)
                const ahora = new Date();
                const fechaFormateada = ahora.toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
                const horaFormateada = ahora.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit'
                });

                // Obtener código de facturación existente o generar uno temporal con CRC32
                let codigoFacturacion;
                if (infoPedido.codigopedidofactura) {
                  codigoFacturacion = infoPedido.codigopedidofactura;
                } else if (infoPedido.idPedido) {
                  // Generar CRC32 de MySQL temporal del ID del pedido (solo números)
                  const generarCRC32MySQL = (str) => {
                    const crcTable = [];
                    for (let i = 0; i < 256; i++) {
                      let crc = i;
                      for (let j = 0; j < 8; j++) {
                        crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
                      }
                      crcTable[i] = crc;
                    }
                    let crc = 0xFFFFFFFF;
                    for (let i = 0; i < str.length; i++) {
                      const byte = str.charCodeAt(i);
                      crc = crcTable[(crc ^ byte) & 0xFF] ^ (crc >>> 8);
                    }
                    return (crc ^ 0xFFFFFFFF) >>> 0;
                  };
                  codigoFacturacion = generarCRC32MySQL(infoPedido.idPedido.toString()).toString();
                } else {
                  codigoFacturacion = 'TEMP';
                }

                // Información de facturación
                leyendasTexto.push(centrarTexto(`#TICKET: ${infoPedido.idPedido || 'TEMP'}`));
                leyendasTexto.push(centrarTexto(`TIENDA: ${codigoSucursal}`));
                leyendasTexto.push(centrarTexto(`FECHA: ${fechaFormateada} `));

                // Dividir código de facturación si es muy largo
                const lineasCodigo = dividirTexto(`COD.FACT: ${codigoFacturacion}`, anchoTicket);
                lineasCodigo.forEach(lineaCodigo => {
                  leyendasTexto.push(centrarTexto(lineaCodigo));
                });

                // Separador final antes de la segunda leyenda con margen
                leyendasTexto.push(conMargen('='.repeat(anchoTicket)));
              }

              // Agregar Leyenda si existe
              if (systemConfig.Leyenda && systemConfig.Leyenda.trim()) {
                const lineasLeyenda = systemConfig.Leyenda.split('\n');
                lineasLeyenda.forEach(linea => {
                  if (linea.trim()) {
                    // Dividir líneas largas respetando el ancho del ticket
                    const lineasDivididas = dividirTexto(linea.trim(), anchoTicket);
                    lineasDivididas.forEach(lineaDividida => {
                      leyendasTexto.push(centrarTexto(lineaDividida));
                    });
                  }
                });
              }

              // Siempre agregar separador final con margen
              leyendasTexto.push(conMargen('='.repeat(anchoTicket)));

              return leyendasTexto.length > 2 ? '\n' + leyendasTexto.join('\n') : '';
            }

            return '';
          } catch (error) {
            console.error('Error al leer systemConfig:', error);
            return '';
          }
        })()}

`;

      // Verificar API de Electron
      const electronApi = window.electron ? window.electron.ipcRenderer : null;
      if (!electronApi) {
        throw new Error('Electron API no está disponible.');
      }

      console.log(`[TICKET-TIMING] Generación de texto: ${(performance.now() - tStart).toFixed(1)}ms`);

      // Obtener impresora predeterminada (usa caché interno, es instantáneo)
      const tPrinters = performance.now();
      const printers = await electronApi.invoke('get-all-printers');
      const defaultPrinter = printers.find(p => p.isDefault) || printers[0];
      console.log(`[TICKET-TIMING] get-all-printers: ${(performance.now() - tPrinters).toFixed(1)}ms`);

      if (!defaultPrinter) {
        throw new Error("No se encontró impresora predeterminada");
      }

      // Construir HTML con logo si está configurado
      const ticketLogoBase64 = localStorage.getItem('ticket_logo_base64');
      const ticketLogoEnabled = localStorage.getItem('ticket_logo_enabled') === 'true';
      const logoHtml = ticketLogoBase64 && ticketLogoEnabled
        ? `<div style="padding-left:0;width:280px;text-align:center;margin-bottom:4px;"><img src="${ticketLogoBase64}" style="max-width:140px;height:auto;" /></div>`
        : '';
      const ticketHtmlContent = `${logoHtml}<pre style="font-family: monospace; white-space: pre-wrap; font-size: 13px;">${ticketContent}</pre>`;

      // Enviar a imprimir (fire-and-forget en el backend, respuesta instantánea)
      const tInvoke = performance.now();
      electronApi.invoke('print-document', {
        printerName: defaultPrinter.name,
        htmlContent: ticketHtmlContent,
        silent: true
      }).then(() => {
        console.log(`[TICKET-TIMING] print-document invoke resuelto: ${(performance.now() - tInvoke).toFixed(1)}ms`);
      }).catch(err => console.error('Error imprimiendo ticket:', err.message));

      // Guardar archivo en segundo plano (no bloquea)
      electronApi.invoke('save-file', {
        fileName: 'ultimoTicket.txt',
        content: ticketContent
      }).catch(() => {});

      // Imprimir copia si está configurado
      try {
        const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
        const imprimirCopia = systemConfig.imprimirCopia === 1 || systemConfig.imprimirCopia === '1';

        if (imprimirCopia) {
          electronApi.invoke('print-document', {
            printerName: defaultPrinter.name,
            htmlContent: ticketHtmlContent,
            silent: true
          }).catch(err => console.error('Error imprimiendo copia:', err.message));
        }
      } catch (configError) {
        // Ignorar error de configuración
      }

      console.log(`[TICKET-TIMING] TOTAL función generarEImprimirTicketFinal: ${(performance.now() - tStart).toFixed(1)}ms`);

    } catch (error) {
      console.error(`[TICKET-TIMING] ERROR después de ${(performance.now() - tStart).toFixed(1)}ms:`, error);
      toast.error("Error al generar e imprimir ticket");
    }
  }, [pedidoActual, productosGuardados, infoPedido, tipoPedido, toast]);
  // Función para imprimir comandas por impresora
  const imprimirComandasPorImpresora = useCallback(async (productos) => {
    try {
      // Agrupar productos por impresora
      const productosPorImpresora = {};

      productos.forEach(producto => {
        // Determinar la impresora (similar a PedidosMesa.js)
        const nombreImpresora = producto.impresora || 'DEFAULT';

        if (!productosPorImpresora[nombreImpresora]) {
          productosPorImpresora[nombreImpresora] = [];
        }

        productosPorImpresora[nombreImpresora].push(producto);
      });


      // Imprimir en cada impresora
      const promesasImpresion = [];

      for (const [nombreImpresora, productosImpresora] of Object.entries(productosPorImpresora)) {
        // Solo imprimir si el producto requiere impresión en cocina
        const productosParaImprimir = productosImpresora.filter(p =>
          p.imprimirCocina === 1 || p.imprimirCocina === true || p.imprimirCocina === "1"
        );

        if (productosParaImprimir.length > 0) {
          console.log('🔍 DEBUG - infoPedido completo:', infoPedido);
          console.log('🔍 DEBUG - mesero:', infoPedido.mesero);
          console.log('🔍 DEBUG - nombreMesero:', infoPedido.nombreMesero);

          // Obtener nombre del cliente según tipo de pedido
          const obtenerNombreCliente = () => {
            if (tipoPedido === 'llevar') return infoPedido.clienteLlevar || infoPedido.nombreCliente || '';
            if (tipoPedido === 'domicilio') return infoPedido.clienteDomicilio || infoPedido.nombreCliente || '';
            if (tipoPedido === 'mesa') return infoPedido.nombreCliente || '';
            return infoPedido.nombreCliente || infoPedido.clienteLlevar || infoPedido.clienteDomicilio || '';
          };

          const promesa = comandaService.imprimirComanda(
            productosParaImprimir,
            {
              numero: infoPedido.mesa?.numero || infoPedido.numeroMesa,
              caption: infoPedido.mesa?.caption || infoPedido.mesa?.nombre || infoPedido.nombreMesa
            },
            {
              nombre: infoPedido.nombreMesero || infoPedido.mesero?.nombre || infoPedido.mesero || 'Sin mesero asignado',
              idMesero: infoPedido.idMesero || infoPedido.mesero?.idMesero
            },
            new Date().toLocaleDateString(),
            new Date().toLocaleTimeString(),
            nombreImpresora,
            {
              tipoPedido: tipoPedido,
              nombreCliente: obtenerNombreCliente(),
              numeroPedido: infoPedido.numeroDia || infoPedido.idPedido
            }
          );

          promesasImpresion.push(promesa);
        }
      }

      // Esperar a que todas las impresiones terminen
      if (promesasImpresion.length > 0) {
        const resultados = await Promise.allSettled(promesasImpresion);
        console.log("resultadosdasdad:", resultados);

        const exitosas = resultados.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const fallidas = resultados.filter(r => r.status === 'rejected' || !r.value.success).length;

        if (exitosas > 0) {
          toast.success(`Comandas enviadas a ${exitosas} impresora(s)`);
        }

        if (fallidas > 0) {
          toast.warning(`${fallidas} impresora(s) no pudieron imprimir`);
        }
      }

    } catch (error) {
      console.error('❌ Error en impresión de comandas:', error);
      toast.error('Error al enviar comandas a impresión');
    }
  }, [infoPedido, toast]);

  // Función para cargar detalles de un pedido existente


  // Función para cambiar categoría
  const handleCambiarCategoria = useCallback((categoria) => {
    setCategoriaSeleccionada(categoria);
    cargarProductosCategoria(categoria);
  }, [cargarProductosCategoria]);

  // Función para editar instrucciones de un producto
  const handleEditarInstrucciones = useCallback((idPedidoDetalle, instrucciones) => {
    const pedidoActualizado = pedidoActual.map(item => {
      if (item.idPedidoDetalle === idPedidoDetalle) {
        return { ...item, instrucciones };
      }
      return item;
    });

    setPedidoActual(pedidoActualizado);

    // Actualizar productos nuevos si corresponde
    const productosNuevosActualizados = productosNuevos.map(item => {
      if (item.idPedidoDetalle === idPedidoDetalle) {
        return { ...item, instrucciones };
      }
      return item;
    });
    setProductosNuevos(productosNuevosActualizados);

    // Actualizar productos guardados si corresponde
    const productosGuardadosActualizados = productosGuardados.map(item => {
      if (item.idPedidoDetalle === idPedidoDetalle) {
        return { ...item, instrucciones };
      }
      return item;
    });
    setProductosGuardados(productosGuardadosActualizados);

    toast.success("Instrucciones actualizadas");
  }, [pedidoActual, productosNuevos, productosGuardados, toast]);

  // Función para agregar producto al pedido
  const handleAgregarProducto = useCallback(async (producto) => {
    console.log(`🔍 handleAgregarProducto llamado - Status: ${infoPedido.status}, Tipo: ${tipoPedido}, ForceUpdate: ${forceUpdate}`);

    // Para pedidos de mesa: 'pendiente' bloquea agregar productos
    // Para pedidos domicilio: desde 'finalizado' en adelante bloquea agregar productos
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'pendiente') {
      console.log(`🚫 BLOQUEANDO - Status pendiente detectado`);
      toast.warning("No se pueden agregar productos a un pedido pendiente. Debe abrir la cuenta primero.");
      return;
    }

    if (tipoPedido === 'domicilio' && ['finalizado', 'enviado', 'entregado'].includes(infoPedido.status)) {
      toast.warning(`No se pueden agregar productos a un pedido ${infoPedido.status}.`);
      return;
    }
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'entregado') {
      toast.warning("No se pueden agregar productos a un pedido ya entregado.");
      return;
    }

    console.log(`✅ PERMITIENDO agregar producto - Status válido: ${infoPedido.status}`);

    try {
      console.log("🔍 DEBUG handleAgregarProducto - producto recibido:", producto);
      console.log("🔍 DEBUG todas las propiedades del producto:", Object.keys(producto));
      console.log("🔍 DEBUG categoriaSeleccionada:", categoriaSeleccionada);
      console.log("🔍 DEBUG categorias disponibles:", categorias);

      // Buscar la categoría del producto si no está en categoriaSeleccionada
      let categoriaProducto = categoriaSeleccionada;

      if (!categoriaProducto && producto.idCategoria) {
        categoriaProducto = categorias.find(cat => cat.idCategoria === producto.idCategoria);
        console.log("🔍 DEBUG categoría encontrada por idCategoria del producto:", categoriaProducto);
      }

      if (!categoriaProducto) {
        // Si aún no hay categoría, usar la primera de la lista como fallback
        categoriaProducto = categorias[0] || {};
        console.log("🔍 DEBUG usando primera categoría como fallback:", categoriaProducto);
      }

      const nuevoItem = {
        idPedidoDetalle: `temp_${Date.now()}`,
        idProducto: producto.idProducto || producto.idPlatillo,
        nombre: producto.nombre,
        precio: parseFloat(producto.precio),
        cantidad: 1,
        subtotal: parseFloat(producto.precio),
        impreso: false,
        observaciones: [],
        instrucciones: '', // Campo para instrucciones especiales
        // Campos adicionales requeridos para pedido_detalle - ENRIQUECIDOS
        idCategoria: producto.idCategoria || categoriaProducto?.idCategoria || 0,
        nombreCategoria: producto.nombreCategoria || producto.categoria || categoriaProducto?.nombre || '',
        costo: parseFloat(producto.costo) || parseFloat(producto.costoProduccion) || (parseFloat(producto.precio) * 0.35), // 35% del precio como costo estimado
        // Campos para impresión
        impresora: producto.impresora || 'DEFAULT',
        imprimirCocina: producto.imprimirCocina !== undefined ? producto.imprimirCocina : 1
      };

      console.log("✅ DEBUG nuevoItem creado:", nuevoItem);

      const nuevosProductos = [...productosNuevos, nuevoItem];
      setProductosNuevos(nuevosProductos);

      const todosProductos = [...nuevosProductos, ...productosGuardados];
      setPedidoActual(todosProductos);

      // Recalcular totales y promociones
      await recalcularTotalesYPromociones(todosProductos);

      toast.success(`${producto.nombre} agregado al pedido`);
    } catch (error) {
      console.error("Error al agregar producto:", error);
      toast.error("Error al agregar el producto");
    }
  }, [productosNuevos, productosGuardados, toast, recalcularTotalesYPromociones, tipoPedido, infoPedido.status, forceUpdate]);

  // Función para cambiar cantidad de un producto
  const handleCambiarCantidad = useCallback(async (idPedidoDetalle, nuevaCantidad, operacion) => {
    // Para pedidos de mesa: 'pendiente' bloquea modificar productos
    // Para pedidos domicilio: desde 'finalizado' en adelante bloquea modificar productos
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'pendiente') {
      toast.warning("No se pueden modificar productos en un pedido pendiente.");
      return;
    }

    if (tipoPedido === 'domicilio' && ['finalizado', 'enviado', 'entregado'].includes(infoPedido.status)) {
      toast.warning(`No se pueden modificar productos en un pedido ${infoPedido.status}.`);
      return;
    }
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'entregado') {
      toast.warning("No se pueden modificar productos en un pedido ya entregado.");
      return;
    }

    if (nuevaCantidad < 0) return;

    // Si la cantidad es 0, eliminar el producto
    if (nuevaCantidad === 0) {
      handleEliminarProducto(idPedidoDetalle);
      return;
    }

    const nuevosProductosActualizados = productosNuevos.map(item => {
      if (item.idPedidoDetalle === idPedidoDetalle) {
        const nuevoSubtotal = item.precio * nuevaCantidad;
        return { ...item, cantidad: nuevaCantidad, subtotal: nuevoSubtotal };
      }
      return item;
    });

    setProductosNuevos(nuevosProductosActualizados);

    const todosProductos = [...nuevosProductosActualizados, ...productosGuardados];
    setPedidoActual(todosProductos);

    // Recalcular totales y promociones
    await recalcularTotalesYPromociones(todosProductos);
  }, [productosNuevos, productosGuardados, recalcularTotalesYPromociones, tipoPedido, infoPedido.status, forceUpdate]);

  // Función para eliminar producto
  const handleEliminarProducto = useCallback(async (idPedidoDetalle) => {
    // Para pedidos de mesa: 'pendiente' bloquea eliminar productos
    // Para pedidos domicilio: desde 'finalizado' en adelante bloquea eliminar productos
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'pendiente') {
      toast.warning("No se pueden eliminar productos en un pedido pendiente.");
      return;
    }

    if (tipoPedido === 'domicilio' && ['finalizado', 'enviado', 'entregado'].includes(infoPedido.status)) {
      toast.warning(`No se pueden eliminar productos en un pedido ${infoPedido.status}.`);
      return;
    }
    if (tipoPedido !== 'domicilio' && infoPedido.status === 'entregado') {
      toast.warning("No se pueden eliminar productos en un pedido ya entregado.");
      return;
    }

    const nuevosProductosActualizados = productosNuevos.filter(
      item => item.idPedidoDetalle !== idPedidoDetalle
    );

    setProductosNuevos(nuevosProductosActualizados);

    const todosProductos = [...nuevosProductosActualizados, ...productosGuardados];
    setPedidoActual(todosProductos);

    // Recalcular totales y promociones
    await recalcularTotalesYPromociones(todosProductos);

    toast.info("Producto eliminado");
  }, [productosNuevos, productosGuardados, toast, recalcularTotalesYPromociones, tipoPedido, infoPedido.status, forceUpdate]);

  // Función para eliminar producto guardado (requiere autorización)
  const handleEliminarProductoGuardado = useCallback(async (idPedidoDetalle, authData) => {
    try {
      // Usar los datos de autorización del AutorizacionModal
      const payload = {
        tipo: 'cancelarProductoGuardado',
        idPedidoDetalle: idPedidoDetalle,
        usuarioAutorizado: authData.usuario,
        razonCancelacion: authData.razon || null,
        metodoAuth: authData.metodoAuth
      };

      // Llamar al nuevo endpoint de cancelación usando fetch directamente
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        toast.error(`Error al cancelar producto: ${data.message}`);
        return { success: false, message: data.message };
      }

      // Actualizar el estado local
      const productosGuardadosActualizados = productosGuardados.filter(
        item => item.idPedidoDetalle !== idPedidoDetalle
      );

      setProductosGuardados(productosGuardadosActualizados);

      const todosProductos = [...productosNuevos, ...productosGuardadosActualizados];
      setPedidoActual(todosProductos);

      // Recalcular totales y promociones
      await recalcularTotalesYPromociones(todosProductos);

      toast.success(`Producto cancelado por ${authData.usuario.usuario} (${authData.metodoAuth === 'biometric' ? 'Biométrico' : 'Contraseña'})`);

      return { success: true, data: data.data };
    } catch (error) {
      console.error("Error al eliminar producto guardado:", error);
      toast.error("Error de conexión al cancelar el producto");
      return { success: false, message: "Error de conexión" };
    }
  }, [productosNuevos, productosGuardados, toast, recalcularTotalesYPromociones]);

  // Función para limpiar pedido
  const handleLimpiarPedido = useCallback(() => {
    if (productosNuevos.length === 0) {
      toast.info("No hay productos nuevos para limpiar");
      return;
    }

    if (window.confirm("¿Está seguro de limpiar todos los productos nuevos?")) {
      setProductosNuevos([]);
      setPedidoActual(productosGuardados);
      recalcularTotales(productosGuardados);
      toast.info("Productos nuevos eliminados");
    }
  }, [productosNuevos, productosGuardados, toast]);

  // Función para recalcular totales
  const recalcularTotales = useCallback((productos) => {
    const subtotal = productos.reduce((total, item) => total + item.subtotal, 0);
    const descuentos = promocionesAplicadas.reduce((total, promo) => total + promo.descuento, 0);
    const total = subtotal - descuentos;

    setInfoPedido(prev => ({
      ...prev,
      subtotal,
      descuentos,
      total
    }));
  }, [promocionesAplicadas]);



  // Función para guardar pedido
  const handleGuardar = useCallback(async () => {
    if (productosNuevos.length === 0) {
      toast.info("No hay productos nuevos para guardar");
      return;
    }

    // Ejecutar validación personalizada si existe
    if (validacionesPersonalizadas.antesDeGuardar) {
      const validacionOK = await validacionesPersonalizadas.antesDeGuardar();
      if (!validacionOK) {
        return; // Cancelar guardado si la validación falla
      }
    }

    try {
      setLoadingAccion(true);

      // Si no existe el pedido, crearlo primero usando el método específico del tipo
      let idPedido = infoPedido.idPedido;

      console.log("🔧 DEBUG GestionPedidoBase handleGuardar:");
      console.log("  - infoPedido completo:", infoPedido);
      console.log("  - idPedido extraído:", idPedido);
      console.log("  - evaluación !idPedido:", !idPedido);

      if (!idPedido) {
        const metodosAPI = obtenerMetodosAPI(tipoPedido);
        const respuestaCrear = await metodosAPI.crear({
          tipo: tipoPedido,
          ...infoPedido
        });

        // La respuesta viene en formato { success: true, data: pedidoData }
        const nuevoPedido = respuestaCrear;
        idPedido = nuevoPedido.idPedido;
        setInfoPedido(prev => ({ ...prev, ...nuevoPedido }));

        if (onPedidoCreado) {
          onPedidoCreado(nuevoPedido);
        }
      }

      // Guardar los productos nuevos usando el método específico del tipo de pedido
      const metodosAPI = obtenerMetodosAPI(tipoPedido);

      // ========== CALCULAR PROMOCIONES DE MESA PARA PRODUCTOS NUEVOS ==========
      let promocionesParaNuevos = [];
      let productosConPromoMesa = new Map(); // Mapa: índice del producto -> precio con promoción

      if (tipoPedido === 'llevar' && promocionMesaActiva) {
        console.log('🏷️ Toggle de promoción mesa activo - calculando promociones para productos nuevos...');

        // Cargar promociones de mesa
        const promocionesMesa = await cargarPromociones(true);
        console.log('🏷️ Promociones de mesa disponibles:', promocionesMesa);

        if (promocionesMesa.length > 0) {
          // Crear productos con IDs temporales para calcular promociones
          const todosLosProductos = [
            ...productosGuardados,
            ...productosNuevos.map((p, idx) => ({
              ...p,
              idPedidoDetalle: `temp_${idx}`, // ID temporal
              idProducto: p.idProducto || p.idPlatillo
            }))
          ];

          // Calcular promociones
          promocionesParaNuevos = await PromocionesService.calcularPromociones(todosLosProductos, promocionesMesa);
          console.log('🏷️ Promociones calculadas para todos los productos:', promocionesParaNuevos);

          // Identificar productos nuevos con promoción
          productosNuevos.forEach((producto, idx) => {
            const tempId = `temp_${idx}`;

            for (const promo of promocionesParaNuevos) {
              let precioPromo = null;

              if (promo.tipo === '1' || promo.tipo === 1) {
                // Tipo 1: Descuento porcentual
                if (promo.productos && promo.productos.includes(tempId)) {
                  const porcentaje = promo.porcentajeDescuento / 100;
                  precioPromo = parseFloat((producto.precio * (1 - porcentaje)).toFixed(2));
                }
              } else if (promo.tipo === '2' || promo.tipo === 2) {
                // Tipo 2: 2x1 - productos gratis van a precio 0
                if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                  precioPromo = 0;
                }
              } else if (promo.tipo === '3' || promo.tipo === 3) {
                // Tipo 3: Combo - productos gratis van a precio 0
                if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                  precioPromo = 0;
                }
              }

              if (precioPromo !== null) {
                productosConPromoMesa.set(idx, precioPromo);
                console.log(`🏷️ Producto nuevo "${producto.nombre}" tiene promo mesa: $${producto.precio} -> $${precioPromo}`);
                break;
              }
            }
          });
        }
      }
      // ===========================================================================

      // Preparar todos los productos con precios calculados
      const productosParaGuardar = productosNuevos.map((producto, idx) => {
        let precioFinal = producto.precio;

        if (productosConPromoMesa.has(idx)) {
          precioFinal = productosConPromoMesa.get(idx);
        } else {
          for (const promo of promocionesAplicadas) {
            let promocionAplica = false;
            if (promo.tipo === '1' || promo.tipo === 1) {
              if (promo.productos && promo.productos.includes(producto.idPedidoDetalle)) {
                precioFinal = producto.precio * (1 - promo.porcentajeDescuento / 100);
                promocionAplica = true;
              }
            } else if (promo.tipo === '2' || promo.tipo === 2) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0;
                promocionAplica = true;
              }
            } else if (promo.tipo === '3' || promo.tipo === 3) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0;
                promocionAplica = true;
              }
            }
            if (promocionAplica) break;
          }
        }

        return {
          producto,
          datos: {
            idPlatillo: producto.idProducto || producto.idPlatillo,
            nombrePlatillo: producto.nombre,
            cantidad: producto.cantidad,
            precio: producto.precio,
            precioFinal,
            costo: producto.costo || 0,
            idCategoria: producto.idCategoria || 0,
            nombreCategoria: producto.nombreCategoria || '',
            observaciones: producto.observaciones || [],
            instrucciones: producto.instrucciones || ''
          }
        };
      });

      // Guardar todos los productos en PARALELO
      const resultados = await Promise.all(
        productosParaGuardar.map(({ producto, datos }) =>
          metodosAPI.agregarDetalles(idPedido, datos).then(respuesta => ({
            ...producto,
            idPedidoDetalle: respuesta?.idPedidoDetalle || producto.idPedidoDetalle,
            status: 'guardado',
            impreso: false
          }))
        )
      );
      const productosGuardadosResponse = resultados;

      // Actualizar estados: mover nuevos a guardados
      setProductosGuardados(prev => [...prev, ...productosGuardadosResponse]);
      setProductosNuevos([]);

      toast.success("Pedido guardado correctamente");

      // Imprimir comandas en segundo plano (fire-and-forget, no bloquea la UI)
      imprimirComandasPorImpresora(productosNuevos).catch(err =>
        console.error('Error en impresión en segundo plano:', err)
      );

      if (onPedidoActualizado) {
        onPedidoActualizado({ ...infoPedido, idPedido });
      }
    } catch (error) {
      console.error("Error al guardar pedido:", error);
      toast.error("Error al guardar el pedido");
    } finally {
      setLoadingAccion(false);
    }
  }, [productosNuevos, productosGuardados, infoPedido, tipoPedido, promocionMesaActiva, cargarPromociones, promocionesAplicadas, onPedidoCreado, onPedidoActualizado, toast, imprimirComandasPorImpresora, validacionesPersonalizadas]);

  // Función para imprimir comanda (guardar, imprimir y refrescar)
  const handleImprimir = useCallback(async () => {
    if (productosNuevos.length === 0) {
      toast.info("No hay productos nuevos para imprimir");
      return;
    }

    // Ejecutar validación personalizada si existe
    if (validacionesPersonalizadas.antesDeImprimir) {
      const validacionOK = await validacionesPersonalizadas.antesDeImprimir();
      if (!validacionOK) {
        return; // Cancelar impresión si la validación falla
      }
    }

    try {
      setLoadingAccion(true);

      // 1. Verificar si el pedido existe, si no existe crearlo (usando la misma lógica que handleGuardar)
      let idPedidoParaUsar = infoPedido.idPedido;

      if (!idPedidoParaUsar) {
        console.log('🔧 Pedido no existe, creando nuevo pedido...');
        toast.info("Creando pedido...");

        // Usar la misma lógica que handleGuardar para crear el pedido
        const metodosAPI = obtenerMetodosAPI(tipoPedido);
        const respuestaCrear = await metodosAPI.crear({
          tipo: tipoPedido,
          ...infoPedido
        });

        // La respuesta viene en formato { success: true, data: pedidoData }
        const nuevoPedido = respuestaCrear;
        idPedidoParaUsar = nuevoPedido.idPedido;

        // Actualizar tanto el estado como el prop para consistencia
        setInfoPedido(prev => ({ ...prev, ...nuevoPedido }));

        // También actualizar el objeto infoPedido directamente para que esté disponible inmediatamente
        infoPedido.idPedido = nuevoPedido.idPedido;
        infoPedido.numeroDia = nuevoPedido.numeroDia;
        infoPedido.status = nuevoPedido.status || 'abierto';


        if (onPedidoCreado) {
          onPedidoCreado(nuevoPedido);
        }

        toast.success("Pedido creado exitosamente");
      }

      if (!idPedidoParaUsar) {
        throw new Error('No se puede imprimir sin un ID de pedido válido');
      }

      // 2. Guardar los productos en la base de datos usando el método específico del tipo de pedido
      const metodosAPI = obtenerMetodosAPI(tipoPedido);

      // ========== CALCULAR PROMOCIONES DE MESA PARA PRODUCTOS NUEVOS ==========
      let productosConPromoMesa = new Map(); // Mapa: índice del producto -> precio con promoción

      if (tipoPedido === 'llevar' && promocionMesaActiva) {
        console.log('🏷️ [Imprimir] Toggle de promoción mesa activo - calculando promociones...');

        const promocionesMesa = await cargarPromociones(true);
        console.log('🏷️ [Imprimir] Promociones de mesa disponibles:', promocionesMesa);

        if (promocionesMesa.length > 0) {
          const todosLosProductos = [
            ...productosGuardados,
            ...productosNuevos.map((p, idx) => ({
              ...p,
              idPedidoDetalle: `temp_${idx}`,
              idProducto: p.idProducto || p.idPlatillo
            }))
          ];

          const promocionesParaNuevos = await PromocionesService.calcularPromociones(todosLosProductos, promocionesMesa);
          console.log('🏷️ [Imprimir] Promociones calculadas:', promocionesParaNuevos);

          productosNuevos.forEach((producto, idx) => {
            const tempId = `temp_${idx}`;
            for (const promo of promocionesParaNuevos) {
              let precioPromo = null;
              if (promo.tipo === '1' || promo.tipo === 1) {
                if (promo.productos && promo.productos.includes(tempId)) {
                  const porcentaje = promo.porcentajeDescuento / 100;
                  precioPromo = parseFloat((producto.precio * (1 - porcentaje)).toFixed(2));
                }
              } else if (promo.tipo === '2' || promo.tipo === 2) {
                if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                  precioPromo = 0;
                }
              } else if (promo.tipo === '3' || promo.tipo === 3) {
                if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                  precioPromo = 0;
                }
              }
              if (precioPromo !== null) {
                productosConPromoMesa.set(idx, precioPromo);
                console.log(`🏷️ [Imprimir] Producto "${producto.nombre}" con promo: $${producto.precio} -> $${precioPromo}`);
                break;
              }
            }
          });
        }
      }
      // ===========================================================================

      // Preparar productos con precios calculados
      const productosPreparados = productosNuevos.map((producto, idx) => {
        let precioFinal = producto.precio;
        if (productosConPromoMesa.has(idx)) {
          precioFinal = productosConPromoMesa.get(idx);
        } else {
          for (const promo of promocionesAplicadas) {
            let promocionAplica = false;
            if (promo.tipo === '1' || promo.tipo === 1) {
              if (promo.productos && promo.productos.includes(producto.idPedidoDetalle)) {
                precioFinal = producto.precio * (1 - promo.porcentajeDescuento / 100);
                promocionAplica = true;
              }
            } else if (promo.tipo === '2' || promo.tipo === 2) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0;
                promocionAplica = true;
              }
            } else if (promo.tipo === '3' || promo.tipo === 3) {
              if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                precioFinal = 0;
                promocionAplica = true;
              }
            }
            if (promocionAplica) break;
          }
        }
        return {
          producto,
          datos: {
            idPlatillo: producto.idProducto || producto.idPlatillo,
            nombrePlatillo: producto.nombre,
            cantidad: producto.cantidad,
            precio: producto.precio,
            precioFinal,
            costo: producto.costo || 0,
            idCategoria: producto.idCategoria || 0,
            nombreCategoria: producto.nombreCategoria || '',
            observaciones: producto.observaciones || [],
            instrucciones: producto.instrucciones || ''
          }
        };
      });

      // Guardar todos los productos en PARALELO
      const productosGuardadosResponse = await Promise.all(
        productosPreparados.map(({ producto, datos }) =>
          metodosAPI.agregarDetalles(idPedidoParaUsar, datos).then(respuesta => ({
            ...producto,
            idPedidoDetalle: respuesta?.idPedidoDetalle || producto.idPedidoDetalle,
            status: 'guardado',
            impreso: false
          }))
        )
      );

      // 2. Imprimir comandas en segundo plano (no bloquea la UI)
      imprimirComandasPorImpresora(productosNuevos).catch(err =>
        console.error('Error en impresión en segundo plano:', err)
      );

      // 3. Actualizar estados: mover nuevos a guardados y limpiar nuevos
      const productosGuardadosActualizados = [...productosGuardados, ...productosGuardadosResponse];
      setProductosGuardados(productosGuardadosActualizados);
      setProductosNuevos([]);

      // 4. Actualizar pedido actual con todos los productos
      const todosLosProductos = [...productosGuardadosActualizados];
      setPedidoActual(todosLosProductos);

      // 5. Recalcular totales y promociones con los productos actualizados
      await recalcularTotalesYPromociones(todosLosProductos);

      // 6. Los productos ya están actualizados localmente, no necesitamos recargar desde el backend
      // await cargarDetallesPedido(infoPedido.idPedido); // Comentado para evitar sobrescribir el status local

      toast.success("Productos guardados y comandas enviadas a impresión");
    } catch (error) {
      console.error("Error al guardar e imprimir:", error);
      toast.error("Error al guardar productos o enviar a impresión");
    } finally {
      setLoadingAccion(false);
    }
  }, [productosNuevos, productosGuardados, infoPedido, tipoPedido, promocionMesaActiva, cargarPromociones, promocionesAplicadas, obtenerMetodosAPI, imprimirComandasPorImpresora, recalcularTotalesYPromociones, toast, validacionesPersonalizadas]);

  // Función para guardar e imprimir
  const handleGuardarEImprimir = useCallback(async () => {
    try {
      await handleGuardar();
      if (configuracion.requiereImpresion) {
        await handleImprimir();
      }
    } catch (error) {
      // Los errores ya se muestran en handleGuardar y handleImprimir
      // No navegamos automáticamente, solo re-lanzamos el error
      throw error;
    }
  }, [handleGuardar, handleImprimir, configuracion.requiereImpresion]);

  // Función para abrir cuenta (cambiar de pendiente a abierto)
  const handleAbrirCuenta = useCallback(async () => {
    // Verificar si el pedido está cerrado y liquidado
    if (infoPedido.status === 'cerrado' && (infoPedido.liquidado === '1' || infoPedido.liquidado === 1)) {
      toast.warning('No se puede abrir cuenta de un pedido ya cerrado y liquidado');
      return;
    }

    try {
      setLoadingAccion(true);
      const metodosAPI = obtenerMetodosAPI(tipoPedido);
      await metodosAPI.abrirCuenta(infoPedido.idPedido);

      // Actualizar el status del pedido localmente
      // Para domicilio: entregado -> pendiente
      // Para mesa: pendiente -> abierto
      const nuevoStatus = tipoPedido === 'domicilio' ? 'pendiente' : 'abierto';

      // Marcar el timestamp ANTES del cambio para proteger contra sobrescritura
      const timestampCambio = new Date();
      setUltimoCambioStatus(timestampCambio);

      // Usar flushSync para actualización síncrona inmediata
      flushSync(() => {
        console.log(`🔓 ABRIENDO CUENTA:`);
        console.log(`  - Status ANTES: ${infoPedido.status}`);
        console.log(`  - Status NUEVO: ${nuevoStatus}`);
        console.log(`  - Tipo pedido: ${tipoPedido}`);

        setInfoPedido(prev => ({
          ...prev,
          status: nuevoStatus,
          horaCerrado: null
        }));

        // Forzar re-render inmediato para actualizar la UI
        setForceUpdate(prev => {
          const newValue = prev + 1;
          console.log(`  - ForceUpdate: ${prev} → ${newValue}`);
          return newValue;
        });

        console.log(`✅ CUENTA ABIERTA - Nuevo status: ${nuevoStatus}`);
      });

      // Verificar estado inmediatamente después del flushSync
      console.log(`🔍 ===== VERIFICACIÓN POST-ABRIR CUENTA =====`);
      console.log(`  - infoPedido.status: "${infoPedido.status}"`);
      console.log(`  - status esperado: "${nuevoStatus}"`);
      console.log(`  - ¿Estado correcto?: ${infoPedido.status === nuevoStatus}`);
      console.log(`  - Timestamp verificación: ${new Date().toLocaleTimeString()}`);
      console.log(`🔍 ==========================================`);

      // Notificar cambio de status al componente padre
      if (onPedidoActualizado) {
        console.log('📤 Notificando APERTURA DE CUENTA a PedidoMesaDetalle');
        onPedidoActualizado({
          ...infoPedido,
          status: nuevoStatus
        });
      }

      toast.success("Cuenta abierta correctamente. Ya puede agregar productos.");

      // Regresar automáticamente a la vista de categorías para facilitar agregar productos
      if (categoriaSeleccionada) {
        console.log('🔙 Regresando automáticamente a vista de categorías después de abrir cuenta');
        setCategoriaSeleccionada(null);
      }
    } catch (error) {
      console.error("Error al abrir cuenta:", error);
      toast.error("Error al abrir la cuenta: " + (error.message || 'Error desconocido'));
    } finally {
      setLoadingAccion(false);
    }
  }, [infoPedido.idPedido, infoPedido.status, infoPedido.liquidado, tipoPedido, obtenerMetodosAPI, toast]);

  // Función para reimprimir cuenta
  const handleReimprimirCuenta = useCallback(async () => {
    try {
      toast.info("Enviando reimpresión...");
      generarEImprimirTicketFinal().catch(err =>
        console.error('Error al reimprimir cuenta:', err)
      );
      toast.success("Cuenta enviada a impresión.");
    } catch (error) {
      console.error("Error al reimprimir cuenta:", error);
      toast.error("Error al reimprimir la cuenta");
    }
  }, [generarEImprimirTicketFinal, toast]);

  // Función para ir a pagar
  const handleIrAPagar = useCallback(() => {
    // Verificar si el pedido está cerrado y liquidado
    if (infoPedido.status === 'cerrado' && (infoPedido.liquidado === '1' || infoPedido.liquidado === 1)) {
      toast.warning('No se puede proceder al pago de un pedido ya cerrado y liquidado');
      return;
    }

    // Si es pedido de Uber o Rappi, mostrar modal de marcar como pagado
    if (tipoPedido === 'uber' || tipoPedido === 'rappi') {
      setMostrarConfirmacionPago(true);
    } else {
      // Calcular totales con promociones
      const subtotal = pedidoActual.reduce((total, item) => total + (item.subtotal || (item.precio * item.cantidad)), 0);
      const descuentos = promocionesAplicadas.reduce((total, promo) => total + (promo.descuento || 0), 0);
      const totalConDescuento = subtotal - descuentos;

      console.log('💰 Preparando datos de pago:');
      console.log('  - Subtotal:', subtotal);
      console.log('  - Descuentos promociones:', descuentos);
      console.log('  - Total con descuento:', totalConDescuento);
      console.log('  - Productos:', pedidoActual.map(p => ({ nombre: p.nombre, precio: p.precio, subtotal: p.subtotal })));
      console.log('  - Promociones:', promocionesAplicadas);

      // Preparar datos para el pago con información de promociones
      const datosPago = {
        idPedido: infoPedido.idPedido,
        totalCuenta: totalConDescuento,
        productos: pedidoActual.map(p => ({
          ...p,
          subtotal: p.subtotal || (p.precio * p.cantidad)
        })),
        pedidoInfo: {
          idPedido: infoPedido.idPedido,
          numeroMesa: infoPedido.numeroMesa || infoPedido.idMesa,
          nombreMesa: infoPedido.nombreMesa || `Mesa ${infoPedido.numeroMesa || infoPedido.idMesa}`,
          mesero: (infoPedido.mesero && typeof infoPedido.mesero === 'object' ? infoPedido.mesero.nombre : infoPedido.mesero) || infoPedido.nombreMesero || '',
          numeroPersonas: infoPedido.numeroPersonas || 1,
          detalles: pedidoActual,
          descuentos: descuentos,
          tipoPedido: tipoPedido
        },
        subtotal: subtotal,
        total: totalConDescuento,
        descuentos: descuentos,
        promocionesAplicadas: promocionesAplicadas
      };

      // Guardar en localStorage para que FormaPagoConDatos pueda recuperarlo
      localStorage.setItem('datosPago', JSON.stringify(datosPago));
      console.log('💾 Datos de pago guardados en localStorage:', datosPago);

      // Navegar al sistema de pagos
      navigate(`/pagar-pedido/${infoPedido.idPedido}`);
    }
  }, [infoPedido, pedidoActual, promocionesAplicadas, navigate, tipoPedido, toast]);

  // Función para cambiar mesa
  const handleCambiarMesa = useCallback(() => {
    if (tipoPedido !== 'mesa') {
      toast.warning('El cambio de mesa solo está disponible para pedidos de mesa');
      return;
    }

    if (!infoPedido.mesa) {
      toast.warning('No hay mesa asignada para cambiar');
      return;
    }

    console.log('Abriendo modal de cambio de mesa para pedido:', infoPedido.idPedido, 'Mesa actual:', infoPedido.mesa);
    setMostrarModalCambioMesa(true);
  }, [infoPedido.idPedido, infoPedido.mesa, tipoPedido, toast]);

  // Función para manejar el cambio de mesa exitoso
  const handleCambioMesaExitoso = useCallback((nuevaMesa) => {
    console.log('🎉 Mesa cambiada exitosamente desde GestionPedidoBase:', nuevaMesa);

    // Crear el objeto mesa actualizado
    const mesaActualizada = {
      idMesa: nuevaMesa.idMesa,
      numero: nuevaMesa.numero || nuevaMesa.numeroMesa,
      nombre: nuevaMesa.nombre || nuevaMesa.nombreMesa || `Mesa_${nuevaMesa.numero || nuevaMesa.numeroMesa}`,
      caption: nuevaMesa.caption || nuevaMesa.nombre
    };

    // Actualizar la información del pedido con la nueva mesa
    setInfoPedido(prev => ({
      ...prev,
      mesa: mesaActualizada
    }));

    // IMPORTANTE: Notificar al componente padre (PedidoMesaDetalle) del cambio
    if (onPedidoActualizado) {
      console.log('📤 Notificando cambio de mesa a PedidoMesaDetalle');
      onPedidoActualizado({
        ...infoPedido,
        mesa: mesaActualizada
      });
    }

    toast.success(`Mesa cambiada exitosamente a ${mesaActualizada.nombre}`);
    setMostrarModalCambioMesa(false);
  }, [toast, onPedidoActualizado, infoPedido]);

  // Función para mostrar confirmación de cerrar pedido
  const handleFinalizar = useCallback(async () => {
    if (pedidoActual.length === 0 && productosNuevos.length === 0) {
      toast.warning("No se puede cerrar un pedido vacío");
      return;
    }

    // Ejecutar validación personalizada si existe
    if (validacionesPersonalizadas.antesDeFinalizar) {
      const validacionOK = await validacionesPersonalizadas.antesDeFinalizar();
      if (!validacionOK) {
        return; // Cancelar finalización si la validación falla
      }
    }

    setMostrarConfirmacionCerrar(true);
  }, [pedidoActual, productosNuevos, toast, validacionesPersonalizadas]);

  // Función para confirmar finalización (cerrar pedido)
  const confirmarCerrarPedido = useCallback(async () => {
    try {
      setLoadingAccion(true);
      setMostrarConfirmacionCerrar(false);

      // 0. Verificar si existe el pedido
      let idPedidoActual = infoPedido.idPedido;


      if (!idPedidoActual) {
        // Si no hay ID de pedido, significa que nunca se imprimió/guardó
        // En el flujo normal, el usuario debe imprimir primero antes de finalizar
        toast.error("Debe imprimir el pedido antes de finalizarlo");
        return;
      }


      // 1. Si hay productos nuevos, guardarlos primero
      if (productosNuevos.length > 0) {
        toast.info("Guardando productos nuevos...");
        const metodosAPI = obtenerMetodosAPI(tipoPedido);

        // ========== CALCULAR PROMOCIONES DE MESA PARA PRODUCTOS NUEVOS ==========
        let productosConPromoMesa = new Map();

        if (tipoPedido === 'llevar' && promocionMesaActiva) {
          console.log('🏷️ [Cerrar] Toggle de promoción mesa activo - calculando promociones...');

          const promocionesMesa = await cargarPromociones(true);
          if (promocionesMesa.length > 0) {
            const todosLosProductos = [
              ...productosGuardados,
              ...productosNuevos.map((p, idx) => ({
                ...p,
                idPedidoDetalle: `temp_${idx}`,
                idProducto: p.idProducto || p.idPlatillo
              }))
            ];

            const promocionesParaNuevos = await PromocionesService.calcularPromociones(todosLosProductos, promocionesMesa);
            console.log('🏷️ [Cerrar] Promociones calculadas:', promocionesParaNuevos);

            productosNuevos.forEach((producto, idx) => {
              const tempId = `temp_${idx}`;
              for (const promo of promocionesParaNuevos) {
                let precioPromo = null;
                if (promo.tipo === '1' || promo.tipo === 1) {
                  if (promo.productos && promo.productos.includes(tempId)) {
                    const porcentaje = promo.porcentajeDescuento / 100;
                    precioPromo = parseFloat((producto.precio * (1 - porcentaje)).toFixed(2));
                  }
                } else if (promo.tipo === '2' || promo.tipo === 2) {
                  if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                    precioPromo = 0;
                  }
                } else if (promo.tipo === '3' || promo.tipo === 3) {
                  if (promo.productosGratis && promo.productosGratis.includes(tempId)) {
                    precioPromo = 0;
                  }
                }
                if (precioPromo !== null) {
                  productosConPromoMesa.set(idx, precioPromo);
                  console.log(`🏷️ [Cerrar] Producto "${producto.nombre}" con promo: $${producto.precio} -> $${precioPromo}`);
                  break;
                }
              }
            });
          }
        }
        // ===========================================================================

        // Preparar productos con precios calculados
        const productosPreparados = productosNuevos.map((producto, idx) => {
          let precioFinal = producto.precio;
          if (productosConPromoMesa.has(idx)) {
            precioFinal = productosConPromoMesa.get(idx);
          } else {
            for (const promo of promocionesAplicadas) {
              let promocionAplica = false;
              if (promo.tipo === '1' || promo.tipo === 1) {
                if (promo.productos && promo.productos.includes(producto.idPedidoDetalle)) {
                  precioFinal = producto.precio * (1 - promo.porcentajeDescuento / 100);
                  promocionAplica = true;
                }
              } else if (promo.tipo === '2' || promo.tipo === 2) {
                if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                  precioFinal = 0;
                  promocionAplica = true;
                }
              } else if (promo.tipo === '3' || promo.tipo === 3) {
                if (promo.productosGratis && promo.productosGratis.includes(producto.idPedidoDetalle)) {
                  precioFinal = 0;
                  promocionAplica = true;
                }
              }
              if (promocionAplica) break;
            }
          }
          return {
            producto,
            datos: {
              idPlatillo: producto.idProducto || producto.idPlatillo,
              nombrePlatillo: producto.nombre,
              cantidad: producto.cantidad,
              precio: producto.precio,
              precioFinal,
              costo: producto.costo || 0,
              idCategoria: producto.idCategoria || 0,
              nombreCategoria: producto.nombreCategoria || '',
              observaciones: producto.observaciones || [],
              instrucciones: producto.instrucciones || ''
            }
          };
        });

        // Guardar todos los productos en PARALELO
        const productosGuardadosResponse = await Promise.all(
          productosPreparados.map(({ producto, datos }) =>
            metodosAPI.agregarDetalles(idPedidoActual, datos).then(respuesta => ({
              ...producto,
              idPedidoDetalle: respuesta?.idPedidoDetalle || producto.idPedidoDetalle,
              status: 'guardado',
              impreso: false
            }))
          )
        );

        // Imprimir comandas en segundo plano (no bloquea la UI)
        imprimirComandasPorImpresora(productosNuevos).catch(err =>
          console.error('Error en impresión en segundo plano:', err)
        );

        // Actualizar estados
        setProductosGuardados(prev => [...prev, ...productosGuardadosResponse]);
        setProductosNuevos([]);
        toast.success("Productos guardados e impresos.");
      }

      // 2. Cambiar estatus del pedido a 'pendiente'
      toast.info("Cerrando pedido...");
      const metodosAPI = obtenerMetodosAPI(tipoPedido);
      await metodosAPI.cerrarPedido(idPedidoActual);

      // 2.1. Generar y guardar código de facturación en segundo plano
      try {
        const systemConfig = JSON.parse(localStorage.getItem('systemConfig') || '{}');
        if (systemConfig.linkFactura === 1 || systemConfig.linkFactura === "1") {
          const generarCRC32MySQL = (str) => {
            const crcTable = [];
            for (let i = 0; i < 256; i++) {
              let crc = i;
              for (let j = 0; j < 8; j++) {
                crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
              }
              crcTable[i] = crc;
            }
            let crc = 0xFFFFFFFF;
            for (let i = 0; i < str.length; i++) {
              const byte = str.charCodeAt(i);
              crc = crcTable[(crc ^ byte) & 0xFF] ^ (crc >>> 8);
            }
            return (crc ^ 0xFFFFFFFF) >>> 0;
          };

          const codigoFacturacion = generarCRC32MySQL(idPedidoActual.toString()).toString();
          // Fire-and-forget: no bloquear el flujo
          API.pedidos.guardarCodigoFacturacion({
            idPedido: idPedidoActual,
            codigoFacturacion: codigoFacturacion,
            fechaGeneracion: new Date().toISOString(),
            tipoPedido: tipoPedido
          }).catch(err => console.error('Error al guardar código facturación:', err));
        }
      } catch (error) {
        console.error('Error al generar código de facturación:', error);
      }

      // ACTUALIZAR ESTADO LOCAL DEL PEDIDO INMEDIATAMENTE
      // Para domicilio: pendiente → finalizado (listo para entrega/pago)
      // Para otros tipos: abierto → pendiente (listo para pago)
      const nuevoStatus = tipoPedido === 'domicilio' ? 'finalizado' : 'pendiente';

      // Marcar el timestamp ANTES del cambio para proteger contra sobrescritura
      const timestampCambio = new Date();
      setUltimoCambioStatus(timestampCambio);

      // Usar flushSync para actualización síncrona inmediata
      flushSync(() => {
        console.log(`🔄 FINALIZANDO PEDIDO:`);
        console.log(`  - Status ANTES: ${infoPedido.status}`);
        console.log(`  - Status NUEVO: ${nuevoStatus}`);
        console.log(`  - Tipo pedido: ${tipoPedido}`);

        setInfoPedido(prev => ({
          ...prev,
          status: nuevoStatus,
          horaCerrado: timestampCambio.toISOString()
        }));

        // Forzar re-render inmediato para actualizar la UI
        setForceUpdate(prev => {
          const newValue = prev + 1;
          console.log(`  - ForceUpdate: ${prev} → ${newValue}`);
          return newValue;
        });

        console.log(`✅ PEDIDO FINALIZADO - Nuevo status: ${nuevoStatus}`);
      });

      // Verificar estado inmediatamente después del flushSync
      console.log(`🔍 ===== VERIFICACIÓN POST-FLUSHSYNC =====`);
      console.log(`  - infoPedido.status: "${infoPedido.status}"`);
      console.log(`  - status esperado: "${nuevoStatus}"`);
      console.log(`  - ¿Estado correcto?: ${infoPedido.status === nuevoStatus}`);
      console.log(`  - Timestamp verificación: ${new Date().toLocaleTimeString()}`);
      console.log(`🔍 =====================================`);

      // 3. Imprimir ticket de cuenta en segundo plano (no bloquea la UI)
      generarEImprimirTicketFinal().catch(err =>
        console.error('Error al imprimir ticket final:', err)
      );

      toast.success("Pedido cerrado correctamente.");

      // 3.5. Notificar cambio de status al componente padre
      if (onPedidoActualizado) {
        console.log('📤 Notificando FINALIZACIÓN a PedidoMesaDetalle');
        onPedidoActualizado({
          ...infoPedido,
          status: nuevoStatus
        });
      }

      // 3.6. Notificar pedido cerrado al componente padre
      if (onPedidoCerrado) {
        console.log('📤 Notificando PEDIDO CERRADO al componente padre');
        onPedidoCerrado({
          ...infoPedido,
          status: nuevoStatus
        });
      }

      // 3.7. Regresar automáticamente a la vista de categorías
      if (categoriaSeleccionada) {
        console.log('🔙 Regresando automáticamente a vista de categorías después de finalizar');
        setCategoriaSeleccionada(null);
      }

      // 4. Preguntar si desea proceder al pago
      setMostrarConfirmacionPago(true);

    } catch (error) {
      console.error("Error al cerrar pedido:", error);
      toast.error("Error al cerrar el pedido: " + (error.message || 'Error desconocido'));
    } finally {
      setLoadingAccion(false);
    }
  }, [productosNuevos, productosGuardados, infoPedido, tipoPedido, promocionMesaActiva, cargarPromociones, promocionesAplicadas, obtenerMetodosAPI, imprimirComandasPorImpresora, toast, onPedidoCreado]);

  // Función para manejar confirmación de pago
  const manejarConfirmacionPago = useCallback(async (procederAlPago) => {
    setMostrarConfirmacionPago(false);

    if (procederAlPago) {
      // Verificar si el pedido está cerrado y liquidado
      if (infoPedido.status === 'cerrado' && (infoPedido.liquidado === '1' || infoPedido.liquidado === 1)) {
        toast.warning('No se puede proceder al pago de un pedido ya cerrado y liquidado');
        return;
      }
      // Si es pedido de Uber o Rappi, marcar como pagado directamente
      if (tipoPedido === 'uber' || tipoPedido === 'rappi') {
        try {
          setLoadingAccion(true);

          // Calcular el total del pedido
          const todosLosProductos = pedidoActual;
          const subtotal = todosLosProductos.reduce((sum, producto) =>
            sum + (producto.precio * producto.cantidad), 0);
          const descuentos = promocionesAplicadas.reduce((total, promo) => total + (promo.descuento || 0), 0);
          const total = subtotal - descuentos;

          // Registrar el pago en la tabla pago
          await API.pagos.registrarPago({
            idPedido: infoPedido.idPedido,
            tipoPago: tipoPedido === 'uber' ? 'UBER' : 'RAPPI',
            monto: total,
            fecha: new Date().toISOString()
          });

          // Actualizar el pedido como liquidado y cerrado
          await API.pedidos.marcarComoLiquidado({
            idPedido: infoPedido.idPedido,
            liquidado: 1
          });

          // Actualizar estado local
          setInfoPedido(prev => ({
            ...prev,
            status: 'cerrado',
            liquidado: 1
          }));

          toast.success(`Pedido de ${tipoPedido === 'uber' ? 'Uber' : 'Rappi'} marcado como pagado y cerrado`);
          navigate(rutaRegreso);
        } catch (error) {
          console.error('Error al marcar pedido como pagado:', error);
          toast.error('Error al registrar el pago');
        } finally {
          setLoadingAccion(false);
        }
      } else {
        // Para otros tipos de pedido, navegar al sistema de pagos
        // Calcular totales con promociones
        const subtotal = pedidoActual.reduce((total, item) => total + (item.subtotal || (item.precio * item.cantidad)), 0);
        const descuentos = promocionesAplicadas.reduce((total, promo) => total + (promo.descuento || 0), 0);
        const totalConDescuento = subtotal - descuentos;

        console.log('💰 [manejarConfirmacionPago] Preparando datos de pago:');
        console.log('  - Subtotal:', subtotal);
        console.log('  - Descuentos promociones:', descuentos);
        console.log('  - Total con descuento:', totalConDescuento);

        // Preparar datos para el pago con información de promociones
        const datosPago = {
          idPedido: infoPedido.idPedido,
          totalCuenta: totalConDescuento,
          productos: pedidoActual.map(p => ({
            ...p,
            subtotal: p.subtotal || (p.precio * p.cantidad)
          })),
          pedidoInfo: {
            idPedido: infoPedido.idPedido,
            numeroMesa: infoPedido.numeroMesa || infoPedido.idMesa,
            nombreMesa: infoPedido.nombreMesa || `Mesa ${infoPedido.numeroMesa || infoPedido.idMesa}`,
            mesero: (infoPedido.mesero && typeof infoPedido.mesero === 'object' ? infoPedido.mesero.nombre : infoPedido.mesero) || infoPedido.nombreMesero || '',
            numeroPersonas: infoPedido.numeroPersonas || 1,
            detalles: pedidoActual,
            descuentos: descuentos,
            tipoPedido: tipoPedido
          },
          subtotal: subtotal,
          total: totalConDescuento,
          descuentos: descuentos,
          promocionesAplicadas: promocionesAplicadas
        };

        // Guardar en localStorage para que FormaPagoConDatos pueda recuperarlo
        localStorage.setItem('datosPago', JSON.stringify(datosPago));
        console.log('💾 [manejarConfirmacionPago] Datos de pago guardados en localStorage:', datosPago);

        navigate(`/pagar-pedido/${infoPedido.idPedido}`);
      }
    } else {
      // Regresar al menú anterior
      navigate(rutaRegreso);
    }
  }, [navigate, rutaRegreso, infoPedido, tipoPedido, pedidoActual, promocionesAplicadas, toast]);

  // Función para confirmar finalización (DEPRECATED - mantener por compatibilidad)
  const confirmarFinalizacion = useCallback(async () => {
    // Redirigir al nuevo flujo
    await confirmarCerrarPedido();
  }, [confirmarCerrarPedido]);



  // Función auxiliar para convertir números a letras (simplificada)
  const numeroALetras = useCallback((num) => {
    const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

    const entero = Math.floor(num);
    if (entero === 0) return 'CERO';
    if (entero < 10) return unidades[entero];
    if (entero < 100) {
      const dec = Math.floor(entero / 10);
      const uni = entero % 10;
      return decenas[dec] + (uni > 0 ? ' Y ' + unidades[uni] : '');
    }
    if (entero < 1000) {
      const cent = Math.floor(entero / 100);
      const resto = entero % 100;
      return centenas[cent] + (resto > 0 ? ' ' + numeroALetras(resto) : '');
    }
    return entero.toString(); // Para números mayores, simplemente retornar como string
  }, []);

  // Función para procesar pago - DESHABILITADA, ahora se navega directamente
  const handleProcesarPago = useCallback(async (datosPago) => {
    // Esta función ahora está deshabilitada porque navegamos directamente
    console.log('⚠️ handleProcesarPago llamado - esto no debería pasar');
    return;
    try {
      setLoadingAccion(true);

      // 1. Procesar el pago - simulación temporal hasta que se agregue el endpoint
      const resultado = {
        exito: true,
        transaccionId: `PAY_${Date.now()}`,
        mensaje: 'Pago procesado correctamente'
      };
      // await API.pagos.procesar({ idPedido: infoPedido.idPedido, ...datosPago });

      // 2. En lugar de cerrar directamente, navegar al sistema de pagos
      // Preparar datos para el pago
      const datosPagoParaLocalStorage = {
        idPedido: infoPedido.idPedido,
        totalCuenta: datosPago.total || 0,
        pedidoInfo: {
          idPedido: infoPedido.idPedido,
          numeroMesa: infoPedido.numeroMesa || infoPedido.idMesa,
          nombreMesa: infoPedido.nombreMesa || `Mesa ${infoPedido.numeroMesa || infoPedido.idMesa}`,
          mesero: (typeof infoPedido.mesero === 'object' ? infoPedido.mesero.nombre : infoPedido.mesero) || infoPedido.nombreMesero,
          numeroPersonas: infoPedido.numeroPersonas || 1,
          detalles: pedidoActual || [],
          descuentos: 0
        },
        subtotal: datosPago.subtotal || 0,
        total: datosPago.total || 0
      };

      // Navegar al sistema de pagos directamente desde el servidor
      navigate(`/pagar-pedido/${infoPedido.idPedido}`);
      return; // Salir aquí para evitar el resto del código legacy

      // ACTUALIZAR ESTADO LOCAL DEL PEDIDO
      setInfoPedido(prev => ({
        ...prev,
        status: 'finalizado',
        horaFinalizado: new Date().toISOString()
      }));

      // 3. Si es un pedido de mesa, liberar la mesa
      if (tipoPedido === 'mesa' && infoPedido.idMesa) {
        try {
          await API.mesas.updateStatus(infoPedido.idMesa, 'abierta');
          console.log(`✅ Mesa ${infoPedido.idMesa} liberada exitosamente`);
        } catch (mesaError) {
          console.error("Error al liberar mesa:", mesaError);
          // No lanzar error, solo advertir
          toast.warning("Pago procesado, pero hubo un problema al liberar la mesa");
        }
      }

      toast.success("Pago procesado correctamente");

      if (onPedidoCerrado) {
        onPedidoCerrado(resultado);
      }

      // 4. Navegar de regreso
      navigate(rutaRegreso);
    } catch (error) {
      console.error("Error al procesar pago:", error);
      toast.error("Error al procesar el pago");
      throw error;
    } finally {
      setLoadingAccion(false);
    }
  }, [infoPedido.idPedido, infoPedido.idMesa, tipoPedido, navigate, rutaRegreso, onPedidoCerrado, toast, obtenerMetodosAPI]);

  // Función para regresar
  const handleRegresar = useCallback(async () => {
    if (productosNuevos.length > 0) {
      try {
        toast.info("Guardando productos nuevos...");
        await handleGuardar();
        toast.success("Productos guardados correctamente");
        navigate(rutaRegreso);
      } catch (error) {
        console.error("Error al guardar:", error);
        toast.error("Error al guardar productos");

        // Mostrar confirmación para salir sin guardar
        const confirmarSalir = window.confirm(
          "No se pudieron guardar los productos por un error.\n\n" +
          "¿Desea salir sin guardar? Se perderán los cambios realizados."
        );

        if (confirmarSalir) {
          navigate(rutaRegreso);
        }
        // Si no confirma, se queda en la pantalla actual
      }
    } else {
      navigate(rutaRegreso);
    }
  }, [productosNuevos, navigate, rutaRegreso, handleGuardar, toast]);

  // Configuración de botones según el tipo de pedido y estado
  const botonesConfig = useMemo(() => {
    const esPendiente = infoPedido.status === 'pendiente';
    const esFinalizado = infoPedido.status === 'finalizado';
    const esEnviado = infoPedido.status === 'enviado';
    const esEntregado = infoPedido.status === 'entregado';
    const esCerrado = infoPedido.status === 'cerrado';
    const estaLiquidado = infoPedido.liquidado === '1' || infoPedido.liquidado === 1;

    // Para domicilio: permite modificar hasta que esté finalizado/enviado/entregado
    // Para mesa/otros: pendiente bloquea modificar
    const puedeModificar = tipoPedido === 'domicilio' ?
      (!esFinalizado && !esEnviado && !esEntregado) :
      (!esPendiente && !esEntregado);

    // Para el botón finalizar:
    // - Debe haber productos en el pedido
    // - No debe haber productos nuevos sin guardar (forzar guardar/imprimir primero)
    const finalizarHabilitado = pedidoActual.length > 0 && productosNuevos.length === 0;

    // DEBUG TEMPORAL
    console.log('🔧 DEBUG FINALIZAR BUTTON:');
    console.log(`  - pedidoActual.length: ${pedidoActual.length}`);
    console.log(`  - productosNuevos.length: ${productosNuevos.length}`);
    console.log(`  - productosGuardados.length: ${productosGuardados.length}`);
    console.log(`  - puedeModificar: ${puedeModificar}`);
    console.log(`  - finalizarHabilitado: ${finalizarHabilitado}`);

    return {
      finalizar: {
        visible: puedeModificar,
        habilitado: finalizarHabilitado
      },
      abrirCuenta: {
        visible: !(esCerrado && estaLiquidado) && (tipoPedido === 'domicilio' ?
          (esFinalizado && !esEnviado && !esEntregado) : // Domicilio: solo si está finalizado pero no enviado/entregado
          esPendiente), // Otros tipos: solo si está pendiente
        habilitado: true
      },
      guardar: {
        visible: puedeModificar,
        habilitado: productosNuevos.length > 0
      },
      imprimir: {
        visible: puedeModificar && configuracion.requiereImpresion,
        habilitado: productosNuevos.length > 0
      },
      guardarEImprimir: {
        visible: puedeModificar && configuracion.requiereImpresion,
        habilitado: productosNuevos.length > 0
      },
      limpiar: {
        visible: puedeModificar,
        habilitado: productosNuevos.length > 0
      },
      reimprimirCuenta: {
        visible: tipoPedido === 'domicilio' ?
          (esFinalizado || esEnviado || esEntregado) :
          esPendiente,
        habilitado: true
      },
      irAPagar: {
        visible: !(esCerrado && estaLiquidado) && (tipoPedido === 'domicilio' ?
          (esFinalizado || (esEntregado && infoPedido.liquidado === '0')) :
          esPendiente),
        habilitado: true
      },
      pagarCuenta: {
        visible: false
      },
      cancelar: {
        visible: false
      },
      regresar: {
        visible: true,
        habilitado: true
      }
    };
  }, [pedidoActual.length, productosNuevos.length, configuracion.requiereImpresion, infoPedido.status, infoPedido.liquidado, tipoPedido, forceUpdate]);

  return (
    <div className="gestion-pedido-base">
      <TomaPedidos
        tipoPedido={tipoPedido}
        infoPedido={{
          ...infoPedido,
          // Información de actualización automática
          actualizandoEnSegundoPlano,
          ultimaActualizacion: ultimaActualizacion.toLocaleTimeString('es-ES')
        }}
        botonesConfig={botonesConfig}
        onFinalizar={handleFinalizar}
        onActualizar={() => actualizarPedidoInteligente(false)}
        onGuardar={handleGuardar}
        onImprimir={handleImprimir}
        onGuardarEImprimir={handleGuardarEImprimir}
        onLimpiar={handleLimpiarPedido}
        onRegresar={handleRegresar}
        onAbrirCuenta={handleAbrirCuenta}
        onReimprimirCuenta={handleReimprimirCuenta}
        onIrAPagar={handleIrAPagar}
        onCambiarMesa={handleCambiarMesa}
        loading={loading || loadingAccion}
      >
        <CreacionPedidoActualizado
          categorias={categorias}
          productos={productos}
          pedidoActual={pedidoActual}
          productosNuevos={productosNuevos}
          productosGuardados={productosGuardados}
          mesaActual={infoPedido.mesa}
          promociones={promociones}
          promocionesAplicadas={promocionesAplicadas}
          calculandoPromociones={calculandoPromociones}
          categoriaSeleccionada={categoriaSeleccionada}
          loading={loading}
          onAgregarProducto={handleAgregarProducto}
          onCambiarCantidad={handleCambiarCantidad}
          onEliminarProducto={handleEliminarProducto}
          onEliminarProductoGuardado={handleEliminarProductoGuardado}
          onLimpiarPedido={handleLimpiarPedido}
          onImprimirComanda={handleImprimir}
          onCambiarCategoria={handleCambiarCategoria}
          onEditarInstrucciones={handleEditarInstrucciones}
          onRecargarPedido={() => cargarDetallesPedido(infoPedido.idPedido)}
          tipoPedido={tipoPedido}
          promocionMesaActiva={promocionMesaActiva}
          onTogglePromocionMesa={handleTogglePromocionMesa}
          deshabilitarAgregarProductos={useMemo(() => {
            const currentStatus = infoPedido.status;
            const shouldDisable = tipoPedido === 'domicilio' ?
              ['finalizado', 'enviado', 'entregado'].includes(currentStatus) :
              (currentStatus === 'pendiente' || currentStatus === 'entregado');

            console.log(`🔍 ===== CONTROL PRODUCTOS =====`);
            console.log(`  - tipoPedido: ${tipoPedido}`);
            console.log(`  - currentStatus: "${currentStatus}"`);
            console.log(`  - shouldDisable: ${shouldDisable}`);
            console.log(`  - ForceUpdate: ${forceUpdate}`);
            console.log(`  - Timestamp: ${new Date().toLocaleTimeString()}`);
            console.log(`  - infoPedido completo:`, infoPedido);
            console.log(`🔍 =============================`);

            return shouldDisable;
          }, [tipoPedido, infoPedido.status, forceUpdate])}
        />
      </TomaPedidos>


      <ConfirmationModal
        isOpen={mostrarConfirmacionCerrar}
        onClose={() => setMostrarConfirmacionCerrar(false)}
        onConfirm={confirmarCerrarPedido}
        message={productosNuevos.length > 0
          ? `¿Está seguro que desea cerrar este pedido?

            Se guardarán ${productosNuevos.length} productos nuevos, se imprimirán las comandas, se cambiará el estatus a '${tipoPedido === 'domicilio' ? 'finalizado' : 'pendiente'}' y se generará el ticket de cuenta.`
          : `¿Está seguro que desea cerrar este pedido? Se cambiará el estatus a '${tipoPedido === 'domicilio' ? 'finalizado' : 'pendiente'}' y se generará el ticket de cuenta.`
        }
        confirmText="Sí, Cerrar Pedido"
        cancelText="Cancelar"
      />

      <ConfirmationModal
        isOpen={mostrarConfirmacionPago}
        onClose={() => setMostrarConfirmacionPago(false)}
        onConfirm={() => manejarConfirmacionPago(true)}
        onCancel={() => manejarConfirmacionPago(false)}
        message={(tipoPedido === 'uber' || tipoPedido === 'rappi')
          ? `Pedido de ${tipoPedido === 'uber' ? 'Uber' : 'Rappi'} cerrado. ¿Marcar como PAGADO y LIQUIDADO?`
          : "Pedido cerrado exitosamente. ¿Desea proceder al pago ahora?"}
        confirmText={(tipoPedido === 'uber' || tipoPedido === 'rappi')
          ? "Sí, Marcar como Pagado"
          : "Sí, Ir al Pago"}
        cancelText="No, Regresar al Menú"
      />

      {mostrarModalCambioMesa && (
        <CambioMesaModal
          visible={mostrarModalCambioMesa}
          onClose={() => setMostrarModalCambioMesa(false)}
          mesaActual={infoPedido.mesa}
          pedidoActual={infoPedido}
          onCambioMesa={handleCambioMesaExitoso}
        />
      )}

      {error && (
        <div className="error-mensaje-global">
          {error}
        </div>
      )}
    </div>
  );
};

export default GestionPedidoBase;